// apps/admin/src/App.tsx
//
// Phase 8.1 Wave 1 Task 01 — admin ops portal App refresh.
// vg-binding: P8.D-150  (Shared @printway/ui — AppShell + Sidebar primitives)
// vg-binding: P8.D-171  (Static SPA — react-router-dom client-side routing)
//
// Phase 1 Wave 11 — admin SPA router + design-faithful AppShell.
//
// Wave 10 (REVERTED): authenticated landing was a single centered card
// with 3 stub link buttons + a generic <main className="flex min-h-screen
// items-center justify-center">. That layout did not match the design
// folder (.vg/phases/01-foundation-auth/design/screenshots/) which shows
// every authenticated page rendered inside a full Sidebar + TopBar +
// content shell (merchant-onboarding.png, merchant-security-2fa-setup.png).
//
// Wave 11 fix: HomePage authenticated state + DeferredFeaturePage now
// render through @printway/ui <AppShell>, with a dedicated admin
// <AdminSidebar> matching DESIGN.md §8 (Sidebar Navigation) — burnt
// orange active state, gray-200 borders, account block in footer.
// Light-mode is locked at the ThemeProvider layer (apps/admin/src/main.tsx)
// since the design references are light-only.

import {
  type ReactNode,
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useState,
} from 'react';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';

// Phase 8.1 Wave 3 Task 14 — Cmd+K Command Palette (cmdk lazy mount).
// vg-binding: CONTEXT:P8.D-23
// vg-binding: CONTEXT:P8.D-29
// vg-binding: CONTEXT:P8.D-151
// vg-binding: CONTEXT:P8.D-153
// vg-binding: CONTEXT:P8.D-173
import { useCommandPaletteHotkey } from './components/cmdk/useCommandPaletteHotkey.js';
import { useTranslation } from './i18n/useTranslation.js';
const CommandPalette = lazy(() =>
  import('./components/cmdk/CommandPalette.js').then((mod) => ({
    default: mod.CommandPalette,
  })),
);

import {
  AccountDropdown,
  AppShell,
  HelpDropdown,
  NotFoundPage,
  RoleGuard,
  TopBar,
  TopBarRightSlot,
  cn,
  useAuth,
  useTimezone,
  PageHeader,
} from '@printway/ui';

// issue #2555 — the topbar was rendering `@printway/ui`'s `NotificationBell`
// (TopBarRightSlot.tsx:151), a design-system STUB whose only input is an
// `unreadCount` prop that nothing passed. It has no store, no fetch and
// hardcoded English copy, so the bell was permanently empty and its dropdown
// always read "You're all caught up".
//
// The real bell — store-backed, i18n'd, deep-linking, 7 filter tabs — lives in
// the admin app and was only referenced by `components/shell/TopBar.tsx`, which
// this shell does not render. `NotificationBell` is dropped from the
// @printway/ui import above so this is the only binding for that name.
import { NotificationBell } from './components/shell/NotificationBell.js';

// Phase 8.x debug B1 — admin-local LanguageSelector replaces the no-op
// `@printway/ui` placeholder so the topbar dropdown actually flips the locale
// and persists to /admin/profile/preferences.
import { AdminLanguageSelector } from './components/shell/AdminLanguageSelector.js';
import { AdminTimezoneSelector } from './components/shell/AdminTimezoneSelector.js';

// Phase 9.3 Wave 9 Task 18 — self-service availability toggle in the topbar,
// self-gated to role=support (US-5, P9.3.D-04). Renders null for everyone else.
// vg-binding: PLAN:task-18
// vg-binding: P9.3.D-08   — self-availability toggle (PATCH /agents/me/availability)
// vg-binding: P9.3.D-04   — role=support self-service home surface (US-5)
import { AvailabilityToggle } from './pages/support/components/AvailabilityToggle.js';

// Auth pages.
import LoginPage from './pages/auth/LoginPage.js';
import Verify2FAPage from './pages/auth/Verify2FAPage.js';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage.js';
import ResetPasswordPage from './pages/auth/ResetPasswordPage.js';

// Setup wizard.
import SetupWizardLayout from './pages/setup/SetupWizardLayout.js';
import SetupCreateAdminPage from './pages/setup/SetupCreateAdminPage.js';
import SetupEnable2FAPage from './pages/setup/SetupEnable2FAPage.js';
import SetupRecoveryCodesPage from './pages/setup/SetupRecoveryCodesPage.js';

// Settings.
import { ChangePasswordPage } from './pages/settings/ChangePasswordPage.js';
import { Setup2FAPage } from './pages/settings/Setup2FAPage.js';
import { BillingInfoPage } from './pages/settings/BillingInfoPage.js';
import SecurityCenterPage from './pages/settings/SecurityCenterPage.js';

// Admin views.
import { AdminUsersPage } from './pages/users/AdminUsersPage.js';
// Phase 8.2 Wave 7 Task 50 — Admin User Detail page (/users/:id).
import { AdminUserDetailPage } from './pages/users/AdminUserDetailPage.js';
import { PendingVendorsPage } from './pages/vendors/PendingVendorsPage.js';

// Billing — Phase 3.2.
import {
  TopupQueuePage,
  TopupReviewQueuePage,
} from './pages/billing/TopupQueuePage.js';
import { DeadLetterQueuePage } from './pages/jobs/DeadLetterQueuePage.js';
// Phase 8.3 Wave 7 Task 54 — Monitoring Webhook Logs page + payload viewer.
// Per CONTEXT.md (Webhook Logs surface) + (4-axis validation).
// vg-binding: CONTEXT:decisions=P8.D-129,P8.D-140
// vg-binding: INTERFACE-STANDARDS:error-shape
import WebhookLogsPage from './pages/monitoring/WebhookLogsPage.js';
// Phase 3.2 Wave 11 Task 33 — Withdraw queue + Chargebacks + Transfer Groups.
import { WithdrawQueuePage } from './pages/billing/WithdrawQueuePage.js';
import { ChargebacksPage } from './pages/billing/ChargebacksPage.js';
import { TransferGroupsPage } from './pages/billing/TransferGroupsPage.js';
// Phase 8.2 Wave 2 Task 09 — Vendor Payouts queue (single approve per row,
// Mark Transferred modal)./190/451/452 +.
import { PayoutsQueuePage } from './pages/billing/PayoutsQueuePage.js';
// Phase 3.2 Wave 11 Task 34 — Fraud / Gateway / IMAP / FX Rates.
// Per CONTEXT.md.
import { FraudSettingsPage } from './pages/billing/FraudSettingsPage.js';
import { WhitelistPage } from './pages/whitelist/WhitelistPage.js';
import { GatewaySettingsPage } from './pages/billing/GatewaySettingsPage.js';
import { ImapConfigPage } from './pages/billing/ImapConfigPage.js';
import { FxRatesPage } from './pages/billing/FxRatesPage.js';

// Phase 4.1 Wave 13 Task 37 — Admin Invoices list (cross-merchant table).
// vg-binding: CONTEXT:decisions=P4.1.D-11,P4.1.D-10
// vg-binding: INTERFACE-STANDARDS:error-shape
// Per CONTEXT.md A1 + #10. Covers.
import { InvoiceListPage } from './pages/billing/InvoiceListPage.js';

// Phase 4.1 Wave 13 Task 38 — Admin Invoice Detail + MarkPaidModal +
// AdminApprovePaymentModal. Endpoints: POST /admin/invoices/:id/payments,
// /payments/:paymentId/approve, /reject. Threshold guard (kế toán + > $10k).
// vg-binding: CONTEXT:decisions=P4.1.D-11,P4.1.D-10,P4.1.D-41
// vg-binding: INTERFACE-STANDARDS:error-shape
// Per CONTEXT.md A2 + #13/#21/#23/#24 + C13.
// Covers.
import { InvoiceDetailPage } from './pages/billing/InvoiceDetailPage.js';

// Phase 8.3 Wave 5 Task 39 — Reports Audit Logs page (Recent + Archive Search).
// vg-binding: CONTEXT:decisions=P8.D-113,P8.D-138,P8.D-191,P8.D-222,P8.D-258,P8.D-480
// vg-binding: INTERFACE-STANDARDS:error-shape
import { AuditLogsPage as ReportsAuditLogsPage } from './pages/reports/AuditLogsPage.js';

// Phase 3.4a Wave 9 Task 25 — Admin RolesPage (Billing + Audit perm groups).
// Per CONTEXT.md P3.4a. (admin RBAC for billing) + P3.4a. (audit:view_admin).
import { AdminRolesPage } from './pages/roles/AdminRolesPage.js';

// Phase 3.4b Wave 7 Tasks 16/17 — Admin credits surfaces (CrossAI iter 5 fix4).
// AdminCreditsPage = cross-merchant dashboard at /admin/credits;
// MerchantCreditTab = per-merchant history surfaced via /admin/merchants/:id/credit
// . Both files existed but were never routed; admin couldn't reach them.
import AdminCreditsPage from './pages/credits/AdminCreditsPage.js';
import { MerchantCreditTab } from './pages/users/MerchantCreditTab.js';

// Phase 3.5 Wave 7 Task 21 — Admin Legal Entities CRUD (67/68).
import { LegalEntitiesPage } from './pages/legal-entities/LegalEntitiesPage.js';

// Phase 4.1 Wave 13 Task 39 — Admin A3 ManualGeneratePage (kế toán ad-hoc
// invoice trigger). Per CONTEXT.md A3. Endpoint Task 21 (POST
// /api/v1/admin/invoices/manual-generate). Sibling InvoiceList (Task 37) +
// InvoiceDetail (Task 38) + AgingReport (Task 40) ship in same wave 13.
import { ManualGeneratePage } from './pages/billing/ManualGeneratePage.js';
import { AgingReportPage } from './pages/billing/AgingReportPage.js';

// Phase 4.1 Wave 14 Task 42 — Admin A6 RefundTaskQueuePage + CompleteRefundModal.
// Per CONTEXT.md A6 + #18 + control 2 (4-eyes
// segregation-of-duties for refund completion). Endpoint Task 16
// (PUT /api/v1/admin/refund-tasks/:id/complete). Covers.
// vg-binding: CONTEXT:decisions=P4.1.D-11,P4.1.D-10,P4.1.D-24
// vg-binding: INTERFACE-STANDARDS:error-shape
import { RefundTaskQueuePage } from './pages/billing/RefundTaskQueuePage.js';

// Phase 4.1 Wave 14 Task 41 — Admin A5 CreditMemoListPage. Issue + approve
// credit memos with accountant $500 threshold + 12-month
// dormancy badge.
// vg-binding: CONTEXT:decisions=P4.1.D-11,P4.1.D-10,P4.1.D-24,P4.1.D-39,P4.1.D-40
// vg-binding: INTERFACE-STANDARDS:error-shape
// Per CONTEXT.md A5. Covers.
import { CreditMemoListPage } from './pages/billing/CreditMemoListPage.js';

// Phase 8.2 Wave 3 Task 19 — Admin Pending Approval Queue page.
// vg-binding: CONTEXT:decisions=P8.D-496,P8.D-490,P8.D-495 (Goal G-20)
import { CreditMemoApprovalsPage } from './pages/billing/CreditMemoApprovalsPage.js';

// Phase 8.2 Wave 4 Task 26 — Admin Pre-payment Requests page.
// vg-binding: CONTEXT:decisions=P8.D-236,P8.D-237,P8.D-241,P8.D-242,P8.D-243
import { PaymentRequestsPage } from './pages/billing/PaymentRequestsPage.js';

// Phase 4.1 Wave 14 Task 43 — Admin A7 MerchantBillingPage (per-merchant
// billing tab composing BillingModeSelector + BillingConfigForm +
// ModeSettlementDialog + MerchantInvoiceHistory).
// Endpoints: PUT /admin/merchants/:id/billing-mode (admin-only mode switch
// with optional settlement_action), POST /admin/merchants/:id/billing-config
// (cycle/due_days/due_day_type/soft_limit/payment_methods), GET billing-config
// + admin/credit-memos for settlement preview + admin/invoices for history.
// vg-binding: CONTEXT:decisions=P4.1.D-11,P4.1.D-10,P4.1.D-24,P4.1.D-29,P4.1.D-30
// vg-binding: INTERFACE-STANDARDS:error-shape
// Per CONTEXT.md A7 + #3 +.
// Covers.
import { MerchantBillingPage } from './pages/billing/MerchantBillingPage.js';

// Phase 8.2 Wave 6 Task 38 — Categories admin page (/products/categories).
// vg-binding: CONTEXT:decisions=P8.D-90,P8.D-294,P8.D-295,P8.D-296,P8.D-297,P8.D-298,P8.D-322
import { CategoriesPage } from './pages/products/CategoriesPage.js';

// dbg-20260803-025413 — routed Create/Edit Category form (replaces the inline
// CategoryDetailPanel). Lazy: the form pulls in TipTap, which the list page
// has no reason to load.
const CategoryFormPage = lazy(() =>
  import('./pages/products/CategoryFormPage.js').then((m) => ({
    default: m.CategoryFormPage,
  })),
);

// Phase 8.2 Wave 6 Task 41 — Product Approval admin page
// (`/products/product-approval`).
// vg-binding: CONTEXT:decisions=P8.D-188,P8.D-216,P8.D-307,P8.D-309,P8.D-310,P8.D-311
import { ProductApprovalPage } from './pages/products/ProductApprovalPage.js';

// Phase 8.2 Wave 9 Task 56 — i18n navigation + RBAC helpers moved into
// components/shell/AdminSidebar.tsx alongside the sidebar tree.

// Phase 8.3 Wave 5 Task 65 — SA-only system.* WS subscriber. Mounts
// inside AdminAppShellLayout so every authenticated route shares the
// same single WS subscription.
// vg-binding: CONTEXT:decisions=P8.D-212,P8.D-260
// vg-binding: INTERFACE-STANDARDS:error-shape
import { SystemEventListener } from './components/SystemEventListener.js';
import { SupportTicketListener } from './features/support/SupportTicketListener.js';
import { NotificationFeedListener } from './features/notifications/NotificationFeedListener.js';

// Admin sidebar — declarative tree with SPA NavLink, collapse/expand,
// lucide icons, Lock affordance for SA-only items. Replaces the prior
// inline AdminSidebar in this file (issues: full reload on click, no
// pre-bound tree, duplicate icons, literal "🔒" in labels).
import { AdminSidebar } from './components/shell/AdminSidebar.js';

// Round-18 (debug 2026-05-23) — TopBar center alert banner. Pulls
// currently published announcements for the admin portal via
// GET /announcements/active and renders the highest-priority one
// dismissible. Refetches on WS system.announcement.new.
import { AdminAnnouncementBanner } from './components/banners/AdminAnnouncementBanner.js';

// Icons for HomePage Quick-Action / Disabled cards. Pulled from
// lucide-react so the admin app has a single icon family.
import {
  ArrowRight as LucideArrowRight,
  Building2 as LucideBuilding2,
  FileText as LucideFileText,
  Settings as LucideSettings,
  ShoppingCart as LucideShoppingCart,
  Store as LucideStore,
  Users as LucideUsers,
} from 'lucide-react';

// Phase 8.3 Wave 6 Task 41 — System Settings 7-tab shell (General /
// Merchant / Vendor today; siblings 42/43 fill remaining tabs in place).
// Loaded eagerly because the page is small and the sidebar entry deep-links
// to per-tab URLs (lazy split would force a Suspense flash on tab change).
// vg-binding: CONTEXT:decisions=P8.D-100,P8.D-101,P8.D-120,P8.D-170
// vg-binding: INTERFACE-STANDARDS:error-shape
import { SettingsPage as SystemSettingsPage } from './pages/system/settings/SettingsPage.js';

// Phase 8.3 Wave 6 Task 51 — Bug Tracker SA-only page + global
// ErrorBoundaryAutoReport wrap. Page is lazy-loaded (only SA users open
// it). Boundary wraps the entire <RouterProvider/> so any render-time
// error in any route auto-reports to /api/v1/admin/bug-tracker/auto-report.
// vg-binding: CONTEXT:decisions=P8.D-126,P8.D-164
// vg-binding: INTERFACE-STANDARDS:error-shape
import { ErrorBoundaryAutoReport } from './lib/error-boundary.js';
const BugTrackerPage = lazy(() =>
  import('./pages/system/BugTrackerPage.js').then((m) => ({
    default: m.BugTrackerPage,
  })),
);

// Phase 8.3 Wave 8 Task 63 — CMS Seller Survey page (Config + Results
// tabs). Singleton survey; consumes task-34 BE endpoints
// (GET/PUT /admin/cms/seller-survey + responses[/export]). Permission
// gate: home_management:manage. Lazy-loaded — page sits behind the
// Home Management sidebar group.
// vg-binding: CONTEXT:decisions=P8.D-426,P8.D-427,P8.D-428,P8.D-429,P8.D-430,P8.D-431
// vg-binding: INTERFACE-STANDARDS:error-shape
const SellerSurveyPage = lazy(() =>
  import('./pages/home-management/SellerSurveyPage.js').then((m) => ({
    default: m.SellerSurveyPage,
  })),
);

// ---------------------------------------------------------------------------
// Phase 8.3 — Lazy imports for the 26 admin pages that exist on disk but
// were not wired into the App.tsx router (root cause of /vg:review 8.3
// F-ROUTER-WIRI). Added 2026-05-18 via /vg:debug 8.3 Cluster A.
// Order mirrors `layouts/sidebar/groups.ts`.
// vg-binding: CONTEXT:decisions=P8.D-16,P8.D-18,P8.D-132,P8.D-382,P8.D-475
// ---------------------------------------------------------------------------

// Reports group (Phase 8.3 Wave 5 task-37/38 + Wave 2 task-10).
// NOTE: ReportsAuditLogsPage already imported synchronously at line 141 and
// wired at /reports/audit-logs — no lazy duplicate needed.
// Reports Analytics page (task-37). Wired 2026-07-09 — previously deferred
// because a types-only `.ts` twin shadowed each component on `.js` import
// (AnalyticsPage + the 3 tabs). Those twins were renamed to `*.types.ts`, so
// `.js` now resolves to the `.tsx` component and the page compiles clean.
const ReportsAnalyticsPage = lazy(() =>
  import('./pages/reports/AnalyticsPage.js'),
);
const ReportsOrderWarningsPage = lazy(() =>
  import('./pages/reports/OrderWarningsPage.js'),
);
const ReportsSyncErrorReportsPage = lazy(() =>
  import('./pages/reports/SyncErrorReportsPage.js'),
);
// Wallet-handover mục 13.2 — accountant SoD activity reports read surface.
const ReportsAccountantActivityPage = lazy(() =>
  import('./pages/reports/AccountantActivityPage.js'),
);
const ReportsExportCenterPage = lazy(() =>
  import('./pages/reports/ExportCenterPage.js').then((m) => ({ default: m.ExportCenterPage })),
);

// Home Management group (Phase 8.3 Wave 7-8 tasks 55-63 — CMS sub-modules).
const HomepagePage = lazy(() =>
  import('./pages/home-management/HomepagePage.js').then((m) => ({ default: m.HomepagePage })),
);
const EbooksPage = lazy(() =>
  import('./pages/home-management/EbooksPage.js'),
);
const BlogPage = lazy(() =>
  import('./pages/home-management/BlogPage.js').then((m) => ({ default: m.BlogPage })),
);
// Blog post editor (create/edit) — drill child routes, not in sidebar.
const CmsBlogPostForm = lazy(() =>
  import('./features/cms-blog/BlogPostForm.js').then((m) => ({ default: m.BlogPostForm })),
);
const HelpCenterPage = lazy(() =>
  import('./pages/home-management/HelpCenterPage.js').then((m) => ({ default: m.HelpCenterPage })),
);
// Help Center article editor (create/edit) — drill child routes, not in sidebar.
const CmsHelpArticleForm = lazy(() =>
  import('./features/cms-help/ArticleForm.js').then((m) => ({ default: m.ArticleForm })),
);
const StoriesPage = lazy(() =>
  import('./pages/home-management/StoriesPage.js').then((m) => ({ default: m.StoriesPage })),
);
const FaqPage = lazy(() =>
  import('./pages/home-management/FaqPage.js').then((m) => ({ default: m.FaqPage })),
);
const FooterPage = lazy(() =>
  import('./pages/home-management/FooterPage.js'),
);
const ContactPage = lazy(() =>
  import('./pages/home-management/ContactPage.js'),
);
const PagesPage = lazy(() =>
  import('./pages/home-management/PagesPage.js').then((m) => ({ default: m.PagesPage })),
);

// Inventory group (Phase 8.3 Wave 6 task-50 + G-23 sub-reports — wired
// 2026-07-30, audit II.6: the five report pages existed but only lived in the
// DEAD router.tsx file, which App.tsx never imports, so they were unreachable).
const InventoryOverviewPage = lazy(() =>
  import('./pages/inventory/InventoryOverviewPage.js'),
);
const InventoryStockLevelReportPage = lazy(() =>
  import('./pages/inventory/StockLevelReportPage.js'),
);
const InventoryValuationReportPage = lazy(() =>
  import('./pages/inventory/ValuationReportPage.js'),
);
const InventoryMovementReportPage = lazy(() =>
  import('./pages/inventory/MovementReportPage.js'),
);
const InventoryAgingReportPage = lazy(() =>
  import('./pages/inventory/AgingReportPage.js'),
);
const InventoryTurnoverReportPage = lazy(() =>
  import('./pages/inventory/TurnoverReportPage.js'),
);

// Settings group (Phase 8.3 Wave 6 task-44/51 — email templates + roles editor).
const SettingsEmailTemplatesPage = lazy(() =>
  import('./pages/settings/EmailTemplatesPage.js').then((m) => ({ default: m.EmailTemplatesPage })),
);
// Email template editor (create/edit) — drill child routes, not in sidebar. dbg #2663.
const SettingsEmailTemplateEditor = lazy(() =>
  import('./pages/settings/EmailTemplateEditor.js').then((m) => ({ default: m.EmailTemplateEditor })),
);
const SettingsRolesPage = lazy(() =>
  import('./pages/settings/RolesPage.js').then((m) => ({ default: m.RolesPage })),
);

// System group (Phase 8.3 Wave 1-5 — admin/maintenance/feature-flags/health).
const SystemAnnouncementsPage = lazy(() =>
  import('./pages/system/AnnouncementsPage.js').then((m) => ({ default: m.AnnouncementsPage })),
);
const SystemDualApprovalQueuePage = lazy(() =>
  import('./pages/system/DualApprovalQueuePage.js').then((m) => ({ default: m.DualApprovalQueuePage })),
);
const SystemAdminMgmtPage = lazy(() =>
  import('./pages/system/AdminMgmtPage.js').then((m) => ({ default: m.AdminMgmtPage })),
);
const SystemFeatureFlagsPage = lazy(() =>
  import('./pages/system/FeatureFlagsPage.js').then((m) => ({ default: m.FeatureFlagsPage })),
);
const SystemHealthPage = lazy(() =>
  import('./pages/system/SystemHealthPage.js').then((m) => ({ default: m.SystemHealthPage })),
);
const SystemMaintenanceModePage = lazy(() =>
  import('./pages/system/MaintenanceModePage.js').then((m) => ({ default: m.MaintenanceModePage })),
);

// Monitoring group (Phase 8.3 Wave 3 task-24 + Wave 5).
const MonitoringQueueJobsPage = lazy(() =>
  import('./pages/monitoring/QueueJobsPage.js').then((m) => ({ default: m.QueueJobsPage })),
);
const MonitoringSyncErrorsPage = lazy(() =>
  import('./pages/monitoring/SyncErrorsMonitoringPage.js'),
);
const MonitoringImapParserPage = lazy(() =>
  import('./pages/monitoring/ImapParserPage.js'),
);

// ---------------------------------------------------------------------------
// Phase 8.2 Wave 9 Task 56 — Router + Sidebar wiring (lazy-loaded admin
// pages from Phase 8.1 + 8.2). Lesson F-007 satisfied: no Coming-soon
// placeholders left — every page reachable via sidebar.
// ---------------------------------------------------------------------------

// Phase 8.1 dashboard + operations surface (F-006/F-007 deferred fix).
const DashboardPage = lazy(() =>
  import('./pages/dashboard/DashboardPage.js').then((m) => ({
    default: m.DashboardPage,
  })),
);
const OrdersListPage = lazy(() =>
  import('./pages/orders/OrdersListPage.js').then((m) => ({
    default: m.OrdersListPage,
  })),
);
const OrderDetailPage = lazy(() =>
  import('./pages/orders/OrderDetailPage.js').then((m) => ({
    default: m.OrderDetailPage,
  })),
);
// Phase 8.2.1 Wave 14 sub-wave 14.C T-J — CSV vendor override
// upload page (vg-binding: CONTEXT:P8.D-546).
//  (Amendment #11) — Order Settings hub. The CSV Vendor Override page
//  is now rendered inside its Bulk Tools tab, so it is imported by
// OrderSettingsPage directly rather than lazy-mounted on its own route here.
const OrderSettingsPage = lazy(() =>
  import('./pages/orders/OrderSettingsPage.js').then((m) => ({
    default: m.OrderSettingsPage,
  })),
);
// Batch E (P1-22, user decision 2026-08-03): OrderAssignRulesPage retired —
// the routing engine reads Blueprint vendorAllocations; /vendors/allocations
// is the ONE config surface. Route below redirects.
// P1-20 fix — Pending Manual Assign queue page (Phase 8.1 Wave 7 Task 37)
// existed on disk but was never registered as a route.
const PendingManualAssignPage = lazy(() =>
  import('./pages/orders/pending-assign/PendingManualAssignPage.js').then(
    (m) => ({
      default: m.PendingManualAssignPage,
    }),
  ),
);
const MerchantsListPage = lazy(() =>
  import('./pages/merchants/MerchantsListPage.js').then((m) => ({
    default: m.MerchantsListPage,
  })),
);
const MerchantDetailPage = lazy(() =>
  import('./pages/merchants/MerchantDetailPage.js').then((m) => ({
    default: m.MerchantDetailPage,
  })),
);
// Phase 8.1 Amendment #1 (part 1) — Merchant Profile editor route.
const MerchantEditPage = lazy(() =>
  import('./pages/merchants/MerchantEditPage.js').then((m) => ({
    default: m.MerchantEditPage,
  })),
);
const StoresListPage = lazy(() =>
  import('./pages/stores/StoresListPage.js').then((m) => ({
    default: m.StoresListPage,
  })),
);
const StoreDetailPage = lazy(() =>
  import('./pages/stores/StoreDetailPage.js').then((m) => ({
    default: m.StoreDetailPage,
  })),
);
const SyncErrorsDlqPage = lazy(() =>
  import('./pages/sync-errors/SyncErrorsDlqPage.js'),
);
const ShipmentTrackingPage = lazy(() =>
  import('./pages/shipments/ShipmentTrackingPage.js').then((m) => ({
    default: m.ShipmentTrackingPage,
  })),
);
const NotificationsFeedPage = lazy(() =>
  import('./pages/notifications/NotificationsFeedPage.js').then((m) => ({
    default: m.NotificationsFeedPage,
  })),
);
const AccountSettingsPage = lazy(() =>
  import('./pages/profile/AccountSettingsPage.js').then((m) => ({
    default: m.AccountSettingsPage,
  })),
);
const LoginHistoryPage = lazy(() =>
  import('./pages/profile/LoginHistoryPage.js').then((m) => ({
    default: m.LoginHistoryPage,
  })),
);
// Profile/preferences consolidated onto the canonical tabbed page
// /settings/profile?tab=preferences (AccountSettingsPage → PreferencesTab).
// The standalone profile/settings PreferencesPage duplicates were orphaned
// (no menu entry / divergent endpoints) — routes below now redirect.
const SessionsPage = lazy(() =>
  import('./pages/profile/SessionsPage.js').then((m) => ({
    default: m.SessionsPage,
  })),
);

// Vendor admin (Phase 8.1).
//
// dbg-20260811-150000 — `/vendors` is now the figma-v3-vendor-admin.pen list
// (VendorCatalogPage, eight columns). The 16-column tabbed view with the bulk
// action bar keeps every one of its features at `/vendors/manage`; nothing was
// deleted, the two views just answer different questions.
const VendorCatalogPage = lazy(() =>
  import('./pages/vendors/VendorCatalogPage.js').then((m) => ({
    default: m.VendorCatalogPage,
  })),
);
const ServicesListPage = lazy(() =>
  import('./pages/vendors/ServicesListPage.js').then((m) => ({
    default: m.ServicesListPage,
  })),
);
const ServiceFormPage = lazy(() =>
  import('./pages/vendors/ServiceFormPage.js').then((m) => ({
    default: m.ServiceFormPage,
  })),
);
const VendorsListPage = lazy(() =>
  import('./pages/vendors/VendorsListPage.js').then((m) => ({
    default: m.VendorsListPage,
  })),
);
const VendorCreateWizardPage = lazy(() =>
  import('./pages/vendors/VendorCreateWizardPage.js').then((m) => ({
    default: m.VendorCreateWizardPage,
  })),
);
// dbg (2026-07-07 #294) — Vendor Bulk Import 4-step wizard (Upload → Map →
// Preview → Commit). Page + BE endpoints existed but the route was never
// registered, so there was no way to reach the flow. Wire /vendors/import.
const VendorBulkImportPage = lazy(() =>
  import('./pages/vendors/VendorBulkImportPage.js').then((m) => ({
    default: m.VendorBulkImportPage,
  })),
);
const VendorDetailPage = lazy(() =>
  import('./pages/vendors/VendorDetailPage.js').then((m) => ({
    default: m.VendorDetailPage,
  })),
);
const VendorEditPage = lazy(() =>
  import('./pages/vendors/VendorEditPage.js').then((m) => ({
    default: m.VendorEditPage,
  })),
);
// dbg-admin-vendor-product-detail (2026-06-19) — admin view of a vendor-pushed product.
const VendorProductDetailPage = lazy(() =>
  import('./pages/vendors/VendorProductDetailPage.js').then((m) => ({
    default: m.VendorProductDetailPage,
  })),
);
// dbg (2026-07-06) — orphaned queue pages: built but never route-registered,
// so /vendors/kyc & /vendors/cost-approvals fell through to /vendors/:vendorId
// → "Failed to load vendor: Vendor not found" (UAT #302-308).
const KycQueuePage = lazy(() =>
  import('./pages/vendors/KycQueuePage.js').then((m) => ({
    default: m.KycQueuePage,
  })),
);
const CostApprovalQueuePage = lazy(() =>
  import('./pages/vendors/cost-approval/CostApprovalQueuePage.js').then((m) => ({
    default: m.CostApprovalQueuePage,
  })),
);
// Phase 4.4.2 Wave 5 Task 18 — Vendor Allocations (config split + per-vendor
// stats + change-proposal approvals, 3-tab page). Static route MUST precede
// /vendors/:vendorId (same static-route-order fix as /vendors/kyc, dbg 2026-07-06).
// vg-binding: CONTEXT:decisions=P4.4.2.D-14,P4.4.2.D-16
// vg-binding: INTERFACE-STANDARDS:error-shape
const VendorAllocationsPage = lazy(() =>
  import('./pages/vendors/allocations/VendorAllocationsPage.js').then((m) => ({
    default: m.VendorAllocationsPage,
  })),
);
// Phase 17 Wave 8 Task 30 — Vendor Routing Governance (weights / merchant
// rules / preferences / fence report, 4-tab page). Separate page from
// /vendors/allocations on purpose (P17.D-20): allocation % splits volume among
// eligible vendors, routing governance decides eligibility. Static route MUST
// precede /vendors/:vendorId (same static-route-order fix as /vendors/kyc,
// dbg 2026-07-06).
// vg-binding: CONTEXT:decisions=P17.D-20,P17.D-21,P17.D-26
// vg-binding: INTERFACE-STANDARDS:error-shape
const VendorRoutingPage = lazy(() =>
  import('./pages/vendors/routing/VendorRoutingPage.js').then((m) => ({
    default: m.VendorRoutingPage,
  })),
);

// Phase 8.2 — Catalog (Wave 6 detail-only pages not yet imported above).
const BlueprintsListPage = lazy(() =>
  import('./pages/products/BlueprintsPage.js').then((m) => ({
    default: m.BlueprintsPage,
  })),
);
const BlueprintDetailPage = lazy(() =>
  import('./pages/products/BlueprintDetailPage.js').then((m) => ({
    default: m.BlueprintDetailPage,
  })),
);
const BlueprintCreateWizard = lazy(() =>
  import('./pages/products/BlueprintCreateWizard.js').then((m) => ({
    default: m.BlueprintCreateWizard,
  })),
);
// dbg-wizard-iter4 (promote) — Options entity admin page.
const OptionsPage = lazy(() =>
  import('./pages/products/OptionsPage.js').then((m) => ({
    default: m.OptionsPage,
  })),
);
const ProductsSpecialPage = lazy(() =>
  import('./pages/products/ProductsSpecialPage.js').then((m) => ({
    default: m.ProductsSpecialPage,
  })),
);
const ProductsSpecialDetailPage = lazy(() =>
  import('./pages/products/ProductsSpecialDetailPage.js').then((m) => ({
    default: m.ProductsSpecialDetailPage,
  })),
);
const BundlesPage = lazy(() =>
  import('./pages/products/BundlesPage.js').then((m) => ({
    default: m.BundlesPage,
  })),
);
const MappingPage = lazy(() =>
  import('./pages/products/MappingPage.js').then((m) => ({
    default: m.MappingPage,
  })),
);
const AddonsPage = lazy(() =>
  import('./pages/products/AddonsPage.js').then((m) => ({
    default: m.AddonsPage,
  })),
);
const PricingPage = lazy(() =>
  import('./pages/products/PricingPage.js').then((m) => ({
    default: m.PricingPage,
  })),
);
const TechniquesPage = lazy(() =>
  import('./pages/products/TechniquesPage.js').then((m) => ({
    default: m.TechniquesPage,
  })),
);
const TechniqueDetailPage = lazy(() =>
  import('./pages/products/TechniqueDetailPage.js').then((m) => ({
    default: m.TechniqueDetailPage,
  })),
);
const TechniqueCreatePage = lazy(() =>
  import('./pages/products/TechniqueCreatePage.js').then((m) => ({
    default: m.TechniqueCreatePage,
  })),
);

// Phase 4.7 Wave 6 Task 19 — Shipping Rate Engine v2 admin shell (7 tabs,
// RETIRED from routing by Amendment #3 Task 18: the single
// `/shipping` page is replaced by the 4-child-route structure below. The
// `ShippingPage.tsx` file itself is left in place — Task 27 deletes it once
// the re-home waves (19/20/22) have moved its tab bodies to the new routes.
// The lazy import is removed here so the retired shell no longer ships in the
// admin bundle.

// Phase 4.7 Amendment #3 Wave 2 Task 18 — the single `/shipping`
// 7-tab shell is restructured into a parent `/shipping` route hosting FOUR
// child routes: price / track-routing / config / rate-table. The four route
// page components live in `features/shipping/routes/` and are currently STUB
// placeholders ("coming soon") so the router compiles NOW — later waves
// (19/20/21/22) swap each body for the real page in-place. The old single-page
// `ShippingPage.tsx` is retired from routing (default child → /shipping/price);
// Task 27 deletes the file. Lazy-split so the shipping chunk stays out of the
// dashboard cold-start bundle.
// vg-binding: CONTEXT:decisions=P4.7.D-51,P4.7.D-23,P4.7.D-24,P4.7.D-17
// vg-binding: INTERFACE-STANDARDS:error-shape
const ShippingPriceRoute = lazy(() =>
  import('./features/shipping/routes/ShippingPriceRoute.tsx').then((m) => ({
    default: m.ShippingPriceRoute,
  })),
);
const ShippingTrackRoutingPage = lazy(() =>
  import('./features/shipping/routes/TrackRoutingPage.tsx').then((m) => ({
    default: m.TrackRoutingPage,
  })),
);
const ShippingConfigPage = lazy(() =>
  import('./features/shipping/routes/ConfigPage.tsx').then((m) => ({
    default: m.ConfigPage,
  })),
);
// Phase 4.8 Wave 2 Task 05 — Rate Table 2 (master-detail redesign), the new
// `/shipping/rate-table-2` route running PARALLEL to the 4.7 `/shipping/rate-table`
// (P4.8.D-08). Lazy-split mirroring the 4.7 ShippingRateTablePage pattern; named
// export `RateTable2Page`. The 4.7 route object below stays byte-identical
// (P4.8.D-20 regression guard).
//
// Phase 4.8 Amendment #1 CR-1 (Task 06) — the 3 big top-level tabs inside Rate
// Table 2 are split into SEPARATE sidebar submenus, each with its own native
// route (/shipping/rate-table-2/{routing,margin,history}); Rate becomes an
// independent submenu (/shipping/rate-table-2/rate) keeping its inner sub-tabs.
// RateTable2Page now takes a `section` prop selecting which single section to
// render; the parent /shipping/rate-table-2 redirects → /rate. This gives
// per-submenu deep-link + back/forward for free (P4.8.D-06, P4.8.D-08). The
// 4.7 /shipping/rate-table route object below stays byte-identical (P4.8.D-20).
// vg-binding: CONTEXT:decisions=P4.8.D-06,P4.8.D-08,P4.8.D-19
const ShippingRateTable2Page = lazy(() =>
  import('./features/shipping/routes/RateTable2Page.tsx').then((m) => ({
    default: m.RateTable2Page,
  })),
);
const TemplatesListPage = lazy(() =>
  import('./pages/products/TemplatesPage.js').then((m) => ({
    default: m.TemplatesPage,
  })),
);
// Template detail editor (added via /vg:debug session f931af39). Replaces
// the prior /products/templates/:id route that was aliased back to the
// list page — that alias swallowed every Name-link click on the list.
const TemplateDetailPage = lazy(() =>
  import('./pages/products/TemplateDetailPage.js').then((m) => ({
    default: m.TemplateDetailPage,
  })),
);
// Phase 8.5 — Artwork Doctor catalog pages (real router is App.tsx, not router.tsx).
const MaterialsPage = lazy(() =>
  import('./pages/products/MaterialsPage.js').then((m) => ({
    default: m.MaterialsPage,
  })),
);
const RegionErrorsPage = lazy(() =>
  import('./pages/products/RegionErrorsPage.js').then((m) => ({
    default: m.RegionErrorsPage,
  })),
);
const CheckTypesPage = lazy(() =>
  import('./pages/products/CheckTypesPage.js').then((m) => ({
    default: m.CheckTypesPage,
  })),
);
// Phase 8.4 Amendment #2 — Print Area catalog page.
// vg-binding: CONTEXT:P8.4.D-51
const PrintAreasPage = lazy(() =>
  import('./pages/catalog/PrintAreasPage.js').then((m) => ({
    default: m.PrintAreasPage,
  })),
);

// Phase 8.2.1 Amendment #2 promoted — Admin FulfillmentLocation CRUD.
const LocationsPage = lazy(() =>
  import('./pages/products/LocationsPage.js').then((m) => ({
    default: m.LocationsPage,
  })),
);
const LocationDetailPage = lazy(() =>
  import('./pages/products/LocationDetailPage.js').then((m) => ({
    default: m.LocationDetailPage,
  })),
);

// Phase 8.2.1 debug — Pricing module promoted to a top-level sidebar group.
// Blueprint Prices = read-only aggregation. Shipping fee config lives under the
// /shipping menu (Shipping Price) — the old Pricing > Shipping Fees tab is retired.
const SkuPricesPage = lazy(() =>
  import('./features/pricing/pages/SkuPricesPage.js').then((m) => ({
    default: m.SkuPricesPage,
  })),
);
// Phase 8.2.1 Amendment #16 — Prices V2 page wired in PARALLEL with V1.
// V1 (/pricing/prices) now serves SkuPricesPage (SKU x location list).
// vg-binding: CONTEXT:decisions (P8.D-567 — additive /pricing/prices-v2 route + "Prices V2" sidebar leaf, V1 intact)
// vg-binding: CONTEXT:decisions (P8.D-572 — Prices V2 blueprint-grouped read-only view)
const BlueprintPricesV2Page = lazy(() =>
  import('./pages/pricing/BlueprintPricesV2Page.js').then((m) => ({
    default: m.BlueprintPricesV2Page,
  })),
);
const BlueprintCostPage = lazy(() =>
  import('./pages/pricing/BlueprintCostPage.js').then((m) => ({
    default: m.BlueprintCostPage,
  })),
);
// Phase 14.1 Task 38 (F-O1) — Admin packing-group approval queue (G-32).
// BE fully mounted (app.ts packingGroupsAdminRoutes, MANAGE_PRICING); this
// route/leaf wires the previously-unmounted admin approval surface.
const AdminPackingGroupQueue = lazy(() =>
  import('./pages/pricing/AdminPackingGroupQueue.js').then((m) => ({
    default: m.AdminPackingGroupQueue,
  })),
);

// Phase 14.1 (F-O2 / G-03) — admin pricing-config surface (seasonal fee,
// handling fee, embroidery/digitizing knobs). Component was built but never
// routed (SUMMARY deviation); this wires the standalone page.
const PricingConfigPage = lazy(() =>
  import('./pages/pricing/PricingConfigPage.js').then((m) => ({
    default: m.PricingConfigPage,
  })),
);

// Batch E (P1-22): VendorSettingsPage (tab shell whose only tab embedded the
// retired VendorMappingPage) removed — routes redirect to /vendors/allocations.

// Phase 8.2 — Finance / Billing additional pages (Wave 2/3/4 lazy chunks).
const TransactionsPage = lazy(() =>
  import('./pages/billing/TransactionsPage.js').then((m) => ({
    default: m.TransactionsPage,
  })),
);
const TransactionDetailPage = lazy(() =>
  import('./pages/billing/TransactionDetailPage.js').then((m) => ({
    default: m.TransactionDetailPage,
  })),
);

// Phase 8.2 — Support (Wave 8 dashboard + detail + list).
const SupportDashboardPage = lazy(() =>
  import('./pages/support/SupportDashboardPage.js').then((m) => ({
    default: m.SupportDashboardPage,
  })),
);
const SupportTicketsListPage = lazy(() =>
  import('./pages/support/SupportTicketsPage.js'),
);
const SupportTicketDetailPage = lazy(() =>
  import('./pages/support/SupportTicketDetailPage.js').then((m) => ({
    default: m.SupportTicketDetailPage,
  })),
);
const DepartmentQueuePage = lazy(() =>
  import('./pages/support/DepartmentQueuePage.js'),
);
// dbg-support-port — V2-parity support surfaces (Performance / Configurations).
// Inbox removed 2026-07-10: social/bridge conversations are unified into the
// Tickets queue + ticket detail (SocialTicketThread); no separate inbox page.
const SupportPerformancePage = lazy(() =>
  import('./pages/support/SupportPerformancePage.js'),
);
const SupportConfigurationsPage = lazy(() =>
  import('./pages/support/SupportConfigurationsPage.js'),
);

// Phase 10 review-resume F-02 (round-2) — Sales + Affiliate pages (canonical
// router lives in App.tsx, NOT router.tsx). Pages built Phase 10 waves 7-13
// but route entries were deferred. `.tsx` extensions chosen because each
// page has a `.ts` type-companion (allowImportingTsExtensions=true).
const SalesOverviewPage = lazy(() =>
  import('./pages/sales/Overview.tsx').then((m) => ({ default: m.SalesOverviewPage })),
);
const AdminLeadsPage = lazy(() => import('./pages/sales/Leads/index.tsx'));
const LeadDetailPage = lazy(() =>
  import('./pages/sales/Leads/LeadDetail.tsx').then((m) => ({ default: m.LeadDetailPage })),
);
const AdminDealsPage = lazy(() => import('./pages/sales/Deals/index.tsx'));
const DealDetailPage = lazy(() =>
  import('./pages/sales/Deals/DealDetail.tsx').then((m) => ({ default: m.DealDetailPage })),
);
const SalesGroupsPage = lazy(() =>
  import('./pages/sales/Groups/index.tsx').then((m) => ({ default: m.SalesGroupsPage })),
);
const SalerCalendarPage = lazy(() =>
  import('./pages/sales/Calendar.tsx').then((m) => ({ default: m.SalerCalendarPage })),
);
const SalesTargetsPage = lazy(() =>
  import('./pages/sales/Targets.tsx').then((m) => ({ default: m.SalesTargetsPage })),
);
const SalesLeaderboardPage = lazy(() =>
  import('./pages/sales/Leaderboard.tsx').then((m) => ({ default: m.SalesLeaderboardPage })),
);
const GmvReportPage = lazy(() =>
  import('./pages/sales/GmvReport.tsx').then((m) => ({ default: m.GmvReportPage })),
);
// Phase 10.2 — Sales Operations analytics (Dashboard / Comparison / Forecast).
const OperationsPage = lazy(() =>
  import('./pages/sales/Operations/OperationsPage.tsx').then((m) => ({ default: m.OperationsPage })),
);
const SalerPerformancePage = lazy(() =>
  import('./pages/sales/MyPerformance.tsx').then((m) => ({ default: m.SalerPerformancePage })),
);
const SalerProfilePage = lazy(() =>
  import('./pages/sales/MyProfile.tsx').then((m) => ({ default: m.SalerProfilePage })),
);
const SalesMerchantListPage = lazy(() =>
  import('./pages/sales/Merchants/MerchantList.tsx').then((m) => ({ default: m.MerchantList })),
);
const AdminBenefitsPage = lazy(() =>
  import('./pages/sales/Benefits/index.tsx').then((m) => ({ default: m.AdminBenefitsPage })),
);
const SalesReportsPage = lazy(() =>
  import('./pages/sales/Reports/index.tsx').then((m) => ({ default: m.SalesReportsPage })),
);
const SalesSettingsPage = lazy(() =>
  import('./pages/sales/Settings/index.tsx').then((m) => ({ default: m.SalesSettingsPage })),
);
const QuotationTemplateEditorPage = lazy(() =>
  import('./pages/sales/Settings/QuotationTemplateEditor.tsx').then((m) => ({
    default: m.QuotationTemplateEditor,
  })),
);
const AffiliateAdminPage = lazy(() => import('./pages/affiliate/index.tsx'));
const AffiliateListPage = lazy(() =>
  import('./pages/affiliate/AffiliateList.tsx').then((m) => ({ default: m.AffiliateListPage })),
);
const AffiliateDetailPage = lazy(() => import('./pages/affiliate/AffiliateDetail.tsx'));
const PayoutQueuePage = lazy(() =>
  import('./pages/affiliate/PayoutQueue.tsx').then((m) => ({ default: m.PayoutQueuePage })),
);
const ShortLinksAdminPage = lazy(() =>
  import('./pages/affiliate/ShortLinksAdmin.tsx').then((m) => ({ default: m.ShortLinksAdminPage })),
);
const AbuseReportPage = lazy(() =>
  import('./pages/affiliate/AbuseReport.tsx').then((m) => ({ default: m.AbuseReportPage })),
);

// ---------------------------------------------------------------------------
// withSuspense — wrap a lazy element with a small Suspense fallback. Keeps
// the route list tidy (no inline `<Suspense>` per route). Phase 8.2 Wave 9
// Task 56 — replaces the broken `withSuspense` symbol references left over
// from prior waves' rough-draft route additions.
// ---------------------------------------------------------------------------
function RouteSpinner(): JSX.Element {
  const { t } = useTranslation('admin');
  return (
    <div
      role="status"
      aria-busy="true"
      aria-label={t('common.loading')}
      className="flex min-h-[40vh] items-center justify-center"
    >
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-pw-border border-t-pw-primary" />
    </div>
  );
}
function withSuspense(child: ReactNode): JSX.Element {
  return <Suspense fallback={<RouteSpinner />}>{child}</Suspense>;
}

// Lightweight route wrapper that pulls :merchantId from the URL and feeds
// it into MerchantCreditTab — keeps the tab component drawer-agnostic so a
// future merchant-detail drawer can host it without code changes.
function AdminMerchantCreditRoutePage(): JSX.Element {
  const { t } = useTranslation('admin');
  const { merchantId } = useParams<{ merchantId: string }>();
  if (!merchantId) {
    return (
      <main className="p-6 text-sm text-pw-text-muted">
        Missing merchantId.
      </main>
    );
  }
  return (
    <main className="flex flex-col gap-4 p-6">
      <PageHeader
        title={t('dashboard.merchantCredit')}
        subtitle={t('dashboard.merchantCreditSubtitle')}
      />
      <MerchantCreditTab merchantId={merchantId} />
    </main>
  );
}


function AdminTopBar(): JSX.Element {
  const user = useAuth((s) => s.user);
  const logout = useAuth((s) => s.logout);
  const navigate = useNavigate();
  return (
    <TopBar
      logo={null}
      // Hide bundled ThemeToggle — light mode is locked at provider level.
      showThemeToggle={false}
      userMenu={
        user ? (
          <TopBarRightSlot>
            {/* Admin scope: no Wallet (merchant-only) per Task 103 plan. */}
            {/* Task 18 — availability toggle (self-gated to role=support). */}
            <AvailabilityToggle />
            {/* onViewAll wires the dropdown footer to the Notification Center
                (#2675 B3) — the UI stub this replaced had no such affordance. */}
            <NotificationBell
              onViewAll={() => {
                navigate('/notifications');
              }}
            />
            {/* dbg-20260810 — the design puts the timezone pill immediately
                left of the language selector (frame `header-right`). */}
            <AdminTimezoneSelector />
            <AdminLanguageSelector />
            <HelpDropdown />
            <AccountDropdown
              name={user.displayName || 'Admin'}
              avatarUrl={user.avatarUrl ?? undefined}
              roleBadge="Admin"
              onLogout={() => {
                void (async () => {
                  const { clearSessionHint } = await import(
                    './lib/sessionHint.js'
                  );
                  clearSessionHint();
                  await logout();
                })();
              }}
            />
          </TopBarRightSlot>
        ) : null
      }
    >
      {/* Round-18 — admin announcement banner (DESIGN.md §7.8 slot).
          Renders nothing when the active-feed is empty so the topbar
          collapses back to the right-slot-only layout. */}
      <AdminAnnouncementBanner />
    </TopBar>
  );
}

/**
 * AdminAppShell — wraps child page in <AppShell> with admin-specific
 * <AdminSidebar> + <AdminTopBar>. Pages that need this layout pass
 * children only — the shell handles bg, sidebar, topbar, padding.
 */
function AdminAppShell({ children }: { children: React.ReactNode }): JSX.Element {
  const location = useLocation();
  return (
    <AppShell sidebar={<AdminSidebar activePath={location.pathname} />} topBar={<AdminTopBar />}>
      {children}
    </AppShell>
  );
}

/**
 * AdminAppShellLayout — react-router nested layout route. Wave 12 Task 90:
 * authenticated routes nest under this layout so AdminAppShell renders
 * once around <Outlet/> instead of each page wrapping itself. Fixes BU
 * regression where pages outside HomePage rendered without sidebar/topbar.
 *
 * Auth-guard: if user not yet hydrated → bootstrap; if unauth → redirect
 * /login; if loading → centered spinner (no shell). Shell only renders
 * for authenticated users.
 */
function AdminAppShellLayout(): JSX.Element {
  const user = useAuth((s) => s.user);
  const isLoading = useAuth((s) => s.isLoading);
  const fetchMe = useAuth((s) => s.fetchMe);
  const location = useLocation();
  // dbg-20260810 — subscribes the authenticated layout to the display
  // timezone so switching it repaints every page below.
  //
  // The Intl patch changes what a format call RETURNS, but nothing tells
  // React to call render again: without this the operator picks UTC+1 and
  // the topbar pill updates while every table keeps its old strings until
  // some unrelated state change happens to repaint it.
  //
  // `useTimezone` is a `useSyncExternalStore`, so the subscription re-renders
  // THIS component regardless of whether its parent did — which is what makes
  // it work under RouterProvider, where route elements are stable references
  // React would otherwise bail out on.
  const { timezone } = useTimezone();
  // Must be state (not ref) so the no-session-hint early-return path still
  // triggers a re-render that lets the `!user` branch below redirect to
  // /login. A ref change is invisible to React and would leave the layout
  // stuck on the Loading screen forever (debug-20260520 admin loading hang).
  const [attempted, setAttempted] = useState(false);

  useEffect(() => {
    if (user || attempted) return;
    // Pre-login 401 race fix (B5 / debug-2026-05-20): cold load on `/`
    // renders this layout once before the redirect to `/login`. Without
    // a session hint we'd hit /auth/me + /auth/me/permissions twice (this
    // store + admin-local store) → 4× 401 console errors before the
    // unauthenticated branch takes effect. Skip both bootstrap calls when
    // no hint is present; the !user branch below still redirects.
    void (async () => {
      const { hasSessionHint, clearSessionHint } = await import(
        './lib/sessionHint.js'
      );
      if (!hasSessionHint()) {
        setAttempted(true);
        return;
      }
      try {
        await fetchMe();
      } catch {
        // 401 → cookie expired; drop hint so future cold loads skip.
        clearSessionHint();
      }
      // dbg-20260520-021720 NEW-4 — admin-local useAuthStore needs its own
      // hydrate; shared @printway/ui useAuth populates a different store
      // instance. Components using useAuthStore.hasPermission(...) (Support
      // Dashboard, billing modals, legal-entities) otherwise stay forbidden.
      const { useAuthStore } = await import('./stores/useAuthStore.js');
      // The store's subscriber already mirrored the fetchMe() above when it
      // produced a user (CR1-01); hydrating again = a second /auth/me pair.
      const st = useAuthStore.getState();
      const sharedId = (useAuth.getState().user as { id?: string } | null)?.id ?? null;
      const heldId = (st.user as { id?: string } | null)?.id ?? null;
      if (!st.hydrated || !sharedId || heldId !== sharedId) void st.hydrate();
      setAttempted(true);
    })();
  }, [user, attempted, fetchMe]);

  // dbg-support-polish — the block above only runs on the cold-load path
  // (`!user`). After a fresh LOGIN the shared store already has `user` when
  // this layout first mounts, so the admin-local useAuthStore never
  // hydrated → permissions=[] → Support Dashboard (and every
  // useAuthStore.hasPermission consumer) rendered the forbidden state until
  // a hard reload. Hydrate the admin-local store whenever a user is present
  // and it hasn't hydrated yet.
  //
  // Keyed on the user's id, not just "hydrated once": after an account switch
  // in the same tab the admin store still holds the previous account
  // (2026-10-12), so "already hydrated" is not the same as "hydrated for THIS
  // user".
  const userId = user?.id ?? null;
  useEffect(() => {
    if (!userId) return;
    void (async () => {
      const { useAuthStore } = await import('./stores/useAuthStore.js');
      const st = useAuthStore.getState();
      const heldId = (st.user as { id?: string } | null)?.id ?? null;
      if (st.isLoading) return;
      if (!st.hydrated || heldId !== userId) void st.hydrate();
    })();
  }, [userId]);

  // UAT-1 fix (2026-05-16): only block on Loading during bootstrap (no user
  // yet). Background fetchMe() refreshes also flip isLoading=true; gating on
  // those unmounts the current page mid-action and wipes local state
  // (success toasts, form drafts).
  if ((isLoading && !user) || (!attempted && !user)) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-pw-surface">
        <div className="flex flex-col items-center gap-3">
          <div
            aria-hidden="true"
            className="h-12 w-12 animate-spin rounded-full border-2 border-pw-border border-t-pw-primary"
          />
          <p className="text-sm text-pw-text-muted">Loading…</p>
        </div>
      </main>
    );
  }

  if (!user) {
    // G-21 — preserve the deep link (pathname + search) so LoginPage can
    // bounce back after auth. LoginPage validates `from` is a same-origin
    // in-app path before navigating (no open redirect).
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: `${location.pathname}${location.search}` }}
      />
    );
  }

  // cross-V3 SSO means a merchant/vendor user could land here
  // via shared cookie. RoleGuard renders <WrongPortalPage> when the
  // user lacks admin scope (BE still enforces 403 — this is UX only).
  //
  // Phase 8.3 Wave 5 Task 65 — SystemEventListener mounted here (above
  // <Outlet/>) so SA users receive system.* notifications on every page.
  // Non-SA users see a no-op (the hook short-circuits internally).
  return (
    <RoleGuard user={user} portal="admin">
      <AdminAppShell>
        <SystemEventListener />
        <SupportTicketListener />
        {/* issue #2555 — hydrates the TopBar bell from
            GET /api/v1/notifications + refetches on ws notification.*. Before
            this the bell's store had no REST writer at all and its only WS
            source was the superadmin-gated system channel, so a plain `admin`
            saw an empty bell forever. */}
        <NotificationFeedListener />
        {/* `key={timezone}` remounts the routed page when the display zone
            changes (dbg-20260810).

            Re-rendering this layout is NOT enough on its own: under
            RouterProvider the matched route element is a stable object from
            the router config, so React bails out of that subtree and the
            page keeps its already-formatted strings — measured, not assumed.
            Every alternative that avoids the remount needs each of the 188
            timestamp-rendering files to subscribe individually.

            The remount is cheap and deliberate: TanStack Query serves the
            page's data straight from cache (staleTime 30s), so this is a
            re-render, not a refetch storm. It does discard page-local UI
            state — an open dialog, an unsaved form — which is the accepted
            cost of a global display preference the operator changed on
            purpose. The shell, sidebar and topbar sit ABOVE this key and are
            untouched. */}
        <Outlet key={timezone} />
        {/* Cmd+K palette — mounted INSIDE the router tree (issue #2660):
            CommandPalette calls useNavigate(), which throws
            "may be used only in the context of a <Router>" when rendered
            as a sibling of <RouterProvider>. Hosting it here (a nested
            layout route under RouterProvider) gives it the router context
            and also scopes it to authenticated pages. */}
        <CommandPaletteHost />
      </AdminAppShell>
    </RoleGuard>
  );
}

// ─── Authenticated landing — quick-actions card grid + getting-started ────
//
// Pattern: merchant-onboarding.png — page title (28px semibold) + subtitle
// + Quick Actions grid (3 cards, gap-6) + 2-column "Getting Started"
// (left accordion-ish, right Quick Reference card). Card visuals follow
// DESIGN.md §7.3 (Quick Action Card).
interface QuickActionCardProps {
  title: string;
  description: string;
  href: string;
  icon: JSX.Element;
  accent?: 'primary' | 'neutral';
}

function QuickActionCard({
  title,
  description,
  href,
  icon,
  accent = 'neutral',
}: QuickActionCardProps): JSX.Element {
  return (
    <a
      href={href}
      className="group flex flex-col rounded-xl border border-pw-border bg-pw-bg p-5 transition-colors hover:border-pw-primary/30 hover:bg-pw-surface"
    >
      <div className="flex items-center gap-3">
        <div
          aria-hidden="true"
          className={cn(
            'flex h-10 w-10 items-center justify-center rounded-full',
            accent === 'primary'
              ? 'bg-pw-primary/10 text-pw-primary'
              : 'bg-pw-surface text-pw-text-muted',
          )}
        >
          {icon}
        </div>
        <h3 className="text-base font-semibold text-pw-text">{title}</h3>
      </div>
      <p className="mt-3 text-sm text-pw-text-muted">{description}</p>
      <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-pw-primary-strong group-hover:text-pw-primary-hover">
        Open
        <LucideArrowRight size={14} aria-hidden="true" />
      </span>
    </a>
  );
}

function HomePage(): JSX.Element {
  const user = useAuth((s) => s.user);
  const { t } = useTranslation('admin');

  // Unauth + loading branches handled by AdminAppShellLayout (auth-guard).
  // Wave 12 Task 90: HomePage now content-only when reached via router.
  if (!user) {
    // Defensive — layout should have redirected; render nothing rather than crash.
    return <></>;
  }

  // Authenticated — content only; AdminAppShellLayout (router-level) wraps
  // this in AdminAppShell + Sidebar + TopBar via <Outlet/>. Wave 12 Task 90
  // moved the shell wrap to the layout route to fix BU sidebar regression.
  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-8">
      {/* Page header */}
      <PageHeader
        title={<>Welcome back, {user.displayName || user.email.split('@')[0]}</>}
        subtitle="Manage your Printway platform — users, vendors, and account settings."
      />

      {/* Quick actions — DESIGN.md §7.3 Quick Action Card pattern */}
      <section>
        <h2 className="text-lg font-semibold text-pw-text">{t('dashboard.quickActions')}</h2>
        <p className="text-sm text-pw-text-muted">
          Most-used admin tasks for Phase 1.
        </p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <QuickActionCard
            href="/users"
            title={t('home.manageUsers.title')}
            description={t('home.manageUsers.description')}
            icon={<LucideUsers size={20} aria-hidden="true" />}
            accent="primary"
          />
          <QuickActionCard
            href="/vendors/pending"
            title={t('home.pendingVendors.title')}
            description={t('home.pendingVendors.description')}
            icon={<LucideStore size={20} aria-hidden="true" />}
          />
          <QuickActionCard
            href="/settings/profile"
            title={t('home.accountSettings.title')}
            description={t('home.accountSettings.description')}
            icon={<LucideSettings size={20} aria-hidden="true" />}
          />
        </div>
      </section>

      {/* Coming-soon section (Phase 4+) */}
      <section>
        <h2 className="text-lg font-semibold text-pw-text">{t('dashboard.comingSoon')}</h2>
        <p className="text-sm text-pw-text-muted">
          Features land in later phases. Routes are reserved so navigation
          never 404s.
        </p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <DisabledCard
            title={t('home.merchants.title')}
            description={t('home.merchants.description')}
            phase="Phase 4"
            icon={<LucideBuilding2 size={20} aria-hidden="true" />}
          />
          <DisabledCard
            title={t('home.orders.title')}
            description={t('home.orders.description')}
            phase="Phase 4"
            icon={<LucideShoppingCart size={20} aria-hidden="true" />}
          />
          <DisabledCard
            title={t('home.auditLog.title')}
            description={t('home.auditLog.description')}
            phase="Future"
            icon={<LucideFileText size={20} aria-hidden="true" />}
          />
        </div>
      </section>
    </div>
  );
}

function DisabledCard({
  title,
  description,
  phase,
  icon,
}: {
  title: string;
  description: string;
  phase: string;
  icon: JSX.Element;
}): JSX.Element {
  return (
    <div className="flex flex-col rounded-xl border border-pw-border bg-pw-bg p-5 opacity-70">
      <div className="flex items-center gap-3">
        <div
          aria-hidden="true"
          className="flex h-10 w-10 items-center justify-center rounded-full bg-pw-surface text-pw-text-muted"
        >
          {icon}
        </div>
        <h3 className="text-base font-semibold text-pw-text">{title}</h3>
      </div>
      <p className="mt-3 text-sm text-pw-text-muted">{description}</p>
      <span className="mt-4 inline-flex w-fit items-center rounded-full border border-pw-border bg-pw-surface px-2.5 py-0.5 text-xs font-medium text-pw-text-muted">
        Lands in {phase}
      </span>
    </div>
  );
}

function NotFound(): JSX.Element {
  return (
    <NotFoundPage
      onHome={() => {
        window.location.assign('/');
      }}
    />
  );
}

const router = createBrowserRouter([
  // Public routes — no AppShell.
  { path: '/login', element: <LoginPage /> },
  { path: '/verify-2fa', element: <Verify2FAPage /> },
  { path: '/forgot-password', element: <ForgotPasswordPage /> },
  { path: '/reset-password', element: <ResetPasswordPage /> },
  // Admin invite acceptance — email link is `${ADMIN_BASE_URL}/accept-invite?token=…&type=invite`
  // (admin-user-invite.service.ts). The invite token lives in the same
  // PasswordResetToken collection, so ResetPasswordPage's POST
  // /api/v1/auth/reset-password accepts it directly (set-password = accept-invite).
  { path: '/accept-invite', element: <ResetPasswordPage /> },
  { path: '/index.html', element: <Navigate to="/" replace /> },

  // Setup wizard — own SetupWizardLayout (separate from AdminAppShell).
  {
    path: '/setup',
    element: <SetupWizardLayout />,
    children: [
      { index: true, element: <Navigate to="create-admin" replace /> },
      { path: 'create-admin', element: <SetupCreateAdminPage /> },
      { path: 'enable-2fa', element: <SetupEnable2FAPage /> },
      { path: 'recovery-codes', element: <SetupRecoveryCodesPage /> },
    ],
  },

  // Authenticated layout — AdminAppShell wraps all children via <Outlet/>.
  // Wave 12 Task 90 (BU fix): nested layout route ensures sidebar/topbar
  // render consistently across all admin pages.
  {
    element: <AdminAppShellLayout />,
    children: [
      // F-007 (2026-05-17): wire / + /dashboard → P8.1 DashboardPage
      // (KPIs + charts + activity feed). HomePage retained at /welcome
      // as the Phase 1 placeholder for backward-compat surfaces.
      { path: '/', element: <DashboardPage /> },
      { path: '/dashboard', element: <DashboardPage /> },
      { path: '/welcome', element: <HomePage /> },

      // Admin domain views.
      { path: '/users', element: <AdminUsersPage /> },
      // Phase 8.2 Wave 7 Task 50 — Admin User Detail page (380/475).
      { path: '/users/:id', element: <AdminUserDetailPage /> },
      // Phase 8.1 Wave 4 — `/vendors` general list with tabs (all / active /
      // pending_activation / suspended / kyc / at_risk). Approval-queue page
      // remains at `/vendors/pending` for deep-links and the existing
      // approve/reject drawer flow from Phase 1 Task 50.
      { path: '/vendors', element: withSuspense(<VendorCatalogPage />) },
      // The former `/vendors` — tabs + bulk actions + the wide column set.
      { path: '/vendors/manage', element: <VendorsListPage /> },
      // Shipping-service catalog. Static segments must precede
      // `/vendors/:vendorId` so "services" is not read as a vendorId.
      { path: '/vendors/services', element: withSuspense(<ServicesListPage />) },
      { path: '/vendors/services/new', element: withSuspense(<ServiceFormPage />) },
      {
        path: '/vendors/services/:serviceId/edit',
        element: withSuspense(<ServiceFormPage />),
      },
      { path: '/vendors/pending', element: <PendingVendorsPage /> },
      // Vendor Settings tab shell — static segments must precede
      // `/vendors/:vendorId` so "settings" is not read as a vendorId.
      // Batch E (P1-22): retired config surfaces → the canonical Allocations
      // page (engine reads Blueprint vendorAllocations; BE data kept as archive).
      {
        path: '/vendors/settings',
        element: <Navigate to="/vendors/allocations" replace />,
      },
      {
        path: '/vendors/settings/mapping',
        element: <Navigate to="/vendors/allocations" replace />,
      },

      // Billing — Phase 3.2 Wave 11 Task 32 (Topup queue ships first; sibling
      // pages — withdraw / chargebacks / fraud — land in Tasks 33/34).
      // Phase 4.1 Wave 13 Task 37 adds /billing/invoices and re-points
      // /billing → /billing/invoices (admin invoice management is now
      // the primary billing landing per CONTEXT A1).
      { path: '/billing', element: <Navigate to="/billing/invoices" replace /> },
      { path: '/billing/invoices', element: <InvoiceListPage /> },
      // Phase 4.1 Wave 13 Task 38 — Admin invoice detail with MarkPaid +
      // ApprovePayment modals. Bug 7b (debug-bill iter 5, 2026-05-04):
      // FE has links pointing at BOTH /billing/invoices/:id (Task 37 row
      // click) AND /admin/billing/invoices/:id (legacy nav). Register both
      // so neither 404s. Manual-generate alias is below the :id catch so
      // the literal path matches first.
      { path: '/billing/invoices/manual-generate', element: <ManualGeneratePage /> },
      { path: '/billing/invoices/:id', element: <InvoiceDetailPage /> },
      { path: '/admin/billing/invoices/:id', element: <InvoiceDetailPage /> },
      // Phase 4.1 Wave 14 Task 42 — Admin RefundTaskQueuePage (kế toán bank
      // refund queue). Per CONTEXT A6 + control 2 (4-eyes).
      { path: '/billing/refund-tasks', element: <RefundTaskQueuePage /> },
      // Phase 3.2 FIFO topup work-queue. Route was disabled 2026-07-14 as a
      // "duplicate" of Finance Topup Reviews below — but the two pages are NOT
      // equivalent: only this one has the FIFO (oldest-first) ordering label,
      // CSV export, the suspicious-flag toggle in the detail drawer and the
      // Bị gắn cờ tab. UAT-FIN-014 (#2631) tests exactly those, and hit a 404.
      // Re-enabled 2026-07-25 (dbg-20260725-uat-fin-2629-2632).
      { path: '/billing/topup-queue', element: <TopupQueuePage /> },
      // Phase 8.2 Wave 2 Task 06 — Finance Topup Reviews tab (214/447-450).
      { path: '/billing/topup-reviews', element: <TopupReviewQueuePage /> },
      // Wave 11 Task 33 — withdraw queue + chargebacks + transfer groups.
      { path: '/billing/withdraw-queue', element: <WithdrawQueuePage /> },
      { path: '/billing/chargebacks', element: <ChargebacksPage /> },
      { path: '/billing/transfer-groups', element: <TransferGroupsPage /> },
      // Phase 8.2 Wave 2 Task 09 — Vendor Payouts queue (single approve per row).
      { path: '/billing/payouts', element: <PayoutsQueuePage /> },
      // Wave 11 Task 34 — Fraud / Gateway / IMAP / FX Rates configuration.
      { path: '/billing/fraud-settings', element: <FraudSettingsPage /> },
      // Per-merchant capability whitelist (card payment today). Sits beside
      // fraud settings because both are "who may do what with money", but it
      // is per-merchant where gateway settings are platform-global
      // (decision P4.1.D-21).
      { path: '/whitelist', element: <WhitelistPage /> },
      { path: '/billing/gateways', element: <GatewaySettingsPage /> },
      { path: '/billing/imap-config', element: <ImapConfigPage /> },
      { path: '/billing/fx-rates', element: <FxRatesPage /> },

      // Phase 8.2 Wave 6 Task 38 — Categories admin page (/products/categories).
      // 2-level tree + detail panel + drag-drop reorder same-level (298).
      // SEO defaults. Sidebar entry under Products group.
      { path: '/products/categories', element: <CategoriesPage /> },
      // Static `/new` MUST precede the parametric `/:id/edit` sibling.
      {
        path: '/products/categories/new',
        element: withSuspense(<CategoryFormPage />),
      },
      {
        path: '/products/categories/:id/edit',
        element: withSuspense(<CategoryFormPage />),
      },
      // Collection is the second tab of the same page, not its own list route
      // — the old standalone CollectionsPage called an endpoint that was never
      // registered. The sidebar entry keeps working via this redirect.
      {
        path: '/products/collections/new',
        element: withSuspense(<CategoryFormPage kind="collection" />),
      },
      {
        path: '/products/collections/:id/edit',
        element: withSuspense(<CategoryFormPage kind="collection" />),
      },

      // Phase 8.2 Wave 6 Task 41 — Product Approval queue (vendor submissions
      // + re-approvals). location, 5-column list
      //  5 trigger fields for re-approval diff, duplicate
      // detection banner, reject with 10-500 char notes.
      { path: '/products/product-approval', element: <ProductApprovalPage /> },

      // /admin/audit-log — round-13 (2026-05-23): mount the Phase 8.3 v2
      // page (same component as /reports/audit-logs). The legacy Phase 3.4a
      // `AuditLogPage` sent old query keys (`from`/`entityType`) + unwrapped
      // the old `{success, data:{items}}` envelope, but the BE now returns
      // the canonical `{ok, data:[], meta}` shape → empty table. Wiring the
      // v2 page here keeps the existing URL stable for users while giving
      // them the working multi-tab surface.
      { path: '/admin/audit-log', element: <ReportsAuditLogsPage /> },

      // Phase 8.3 Wave 5 Task 39 — Reports → Audit Logs (Recent + Archive
      // Search tabs + DiffViewer). Consumes the Phase 8.3 BE
      // (GET /api/v1/admin/audit-log + /audit-log/archive-search). Server
      // enforces audit:read (warm) + audit:archive_search (cold); FE gates
      // the Archive Search tab on the same perm hook for UX clarity.
      { path: '/reports/audit-logs', element: <ReportsAuditLogsPage /> },

      // Wallet-handover mục 13.2 — daily accountant SoD activity reports.
      // Server enforces audit:read + admin-tier role.
      {
        path: '/reports/accountant-activity',
        element: <ReportsAccountantActivityPage />,
      },

      // Phase 3.4a Wave 9 Task 25 — Admin RolesPage with Billing + Audit perm
      // groups in the editor (P3.4a. + P3.4a.). Read gated server-side
      // by `permission:list`; the page itself enforces the same client-side.
      { path: '/admin/roles', element: <AdminRolesPage /> },

      // Phase 3.4b Wave 7 Tasks 16/17 — Admin credits surfaces. Cross-merchant
      // dashboard + per-merchant history (CrossAI iter 5 fix4). Server enforces
      // RBAC (`credit:read`); pages render envelope errors via toast.
      { path: '/admin/credits', element: <AdminCreditsPage /> },
      {
        path: '/admin/merchants/:merchantId/credit',
        element: <AdminMerchantCreditRoutePage />,
      },

      // Phase 3.5 Wave 8 Task 23 — Admin Dead Letter Queue (DLQ) retry / discard.
      // Per CONTEXT.md. Backend in Task 24 (BullMQ queue
      // registration). Page surfaces empty state on 404 if BE not yet deployed.
      { path: '/jobs/dlq', element: <DeadLetterQueuePage /> },

      // Phase 8.3 Wave 7 Task 54 — Monitoring Webhook Logs (Shopify / Etsy /
      // WooCommerce / TikTok / Amazon). Per CONTEXT.md (surface)
      // + (4-axis validate: signature/timestamp/ip/idempotency).
      // Permission `monitoring:read` enforced server-side; sidebar visibility
      // gated in layouts/sidebar/groups.ts.
      { path: '/monitoring/webhook-logs', element: <WebhookLogsPage /> },

      // Phase 3.5 Wave 7 Task 21 — Admin Legal Entities CRUD (67/68).
      // Server enforces RBAC via requireRole('admin'); page surfaces 409
      // DUPLICATE_TAX_ID + 412 PRECONDITION_FAILED + 409 LEGAL_ENTITY_IN_USE
      // + 422 CANNOT_DELETE_DEFAULT envelope errors inline.
      { path: '/admin/legal-entities', element: <LegalEntitiesPage /> },

      // Phase 4.1 Wave 13 Task 39 — Admin A3 ManualGeneratePage. Accountant /
      // admin ad-hoc invoice trigger (POST /api/v1/admin/invoices/manual-generate).
      // Per CONTEXT.md A3.
      { path: '/admin/billing/manual-generate', element: <ManualGeneratePage /> },

      // Phase 4.1 Wave 13 Task 40 — Admin A4 AgingReportPage (DSO + buckets,
      // gross + net). Per CONTEXT.md A4.
      { path: '/billing/aging-report', element: <AgingReportPage /> },

      // Phase 4.1 Wave 14 Task 41 — Admin A5 CreditMemoListPage. Issue +
      // approve memos w/ $500 accountant threshold + StaleAlertBadge for
      // dormant memos. Endpoints GET/POST /admin/credit-memos +
      // POST /admin/credit-memos/:id/approve + GET /admin/credit-memos/stale.
      { path: '/billing/credit-memos', element: <CreditMemoListPage /> },

      // Phase 8.2 Wave 3 Task 19 — Admin Pending Approval Queue.
      // Filtered view of credit_memos status=pending_approval with per-row
      // Approve (admin-only) + Reject (reason required 20-1000 chars).
      // Goal. Endpoint GET /admin/credit-memos?status=pending_approval
      // + POST /admin/credit-memos/:id/{approve,reject}.
      {
        path: '/admin/billing/credit-memo-approvals',
        element: <CreditMemoApprovalsPage />,
      },

      // Phase 8.2 Wave 4 Task 26 — Pre-payment Requests page.
      // Document PDF list + create form + PDF preview modal. Permission
      // gate CREATE_PAYMENT_REQUEST enforced FE-side on the
      // create button + BE-side on POST.
      {
        path: '/admin/billing/payment-requests',
        element: <PaymentRequestsPage />,
      },

      // Phase 4.1 Wave 14 Task 43 — Admin A7 MerchantBillingPage. Per-merchant
      // billing tab with mode selector (admin-only) + config form (admin or
      // accountant) + ModeSettlementDialog (net-settle outstanding)
      // + invoice history. RBAC enforced server-side; UI surfaces tooltip
      // "Admin only" on mode-switch button when accountant.
      {
        path: '/admin/merchants/:merchantId/billing',
        element: <MerchantBillingPage />,
      },

      // Settings.
      { path: '/settings', element: <Navigate to="/settings/profile" replace /> },
      { path: '/settings/profile', element: withSuspense(<AccountSettingsPage />) },
      { path: '/settings/login-history', element: withSuspense(<LoginHistoryPage />) },
      { path: '/settings/change-password', element: <ChangePasswordPage /> },
      { path: '/settings/preferences', element: <Navigate to="/settings/profile?tab=preferences" replace /> },
      { path: '/settings/2fa', element: <Setup2FAPage /> },
      { path: '/settings/billing', element: <BillingInfoPage /> },
      { path: '/settings/security', element: <SecurityCenterPage /> },

      // ----------------------------------------------------------------
      // Phase 8.1 admin operations surface (F-006 fix, 2026-05-17).
      // Was: DeferredFeaturePage('Phase 4') placeholders. Now wired to
      // the real P8.1 pages that the build commits already landed under
      // apps/admin/src/pages/{orders,merchants,stores,...}.
      // Permission gating is performed inside each page (or its loaders)
      // rather than per-route AuthGuard so this layer stays consistent
      // with the rest of App.tsx's flat list. RBAC enforcement
      // happens at the BE (server-side 403) for cost-gated reads.
      // ----------------------------------------------------------------
      // Orders
      { path: '/orders', element: <OrdersListPage /> },
      //  (Amendment #11) — Order Settings hub (tabbed; Bulk Tools tab
      // hosts the CSV Vendor Override flow). Literal segments MUST
      // register before the dynamic `:orderId` route.
      { path: '/orders/settings', element: withSuspense(<OrderSettingsPage />) },
      // Legacy redirect — old top-level CSV override route → Order Settings.
      { path: '/orders/csv-vendor-override', element: <Navigate to="/orders/settings" replace /> },
      // P1-20 — Pending Manual Assign queue. Literal segment MUST register
      // before the dynamic `:orderId` route.
      {
        path: '/orders/pending-manual-assign',
        element: withSuspense(<PendingManualAssignPage />),
      },
      { path: '/orders/:orderId', element: withSuspense(<OrderDetailPage />) },
      // Batch E (P1-22): retired — engine never consumed these rules.
      { path: '/order-assign-rules', element: <Navigate to="/vendors/allocations" replace /> },
      // Merchants — both list view (P8.1) AND legacy "Phase 4" placeholder
      // are removed; the list page is canonical now.
      { path: '/merchants', element: <MerchantsListPage /> },
      { path: '/merchants/:merchantId', element: withSuspense(<MerchantDetailPage />) },
      // Phase 8.1 Amendment #1 (part 1) — Merchant Profile editor.
      { path: '/merchants/:merchantId/edit', element: withSuspense(<MerchantEditPage />) },
      // Stores + Sync Errors DLQ + Shipments
      { path: '/stores', element: <StoresListPage /> },
      { path: '/stores/sync-errors-dlq', element: withSuspense(<SyncErrorsDlqPage />) },
      { path: '/stores/:storeId', element: withSuspense(<StoreDetailPage />) },
      { path: '/shipment-tracking', element: withSuspense(<ShipmentTrackingPage />) },
      // Phase 4.7 Amendment #3 Wave 2 Task 18 — Shipping menu group + 4 child
      // routes. The single `/shipping` 7-tab shell is
      // replaced by a parent route whose default child redirects to
      // `/shipping/price`, plus four child routes. The four page components
      // are STUB placeholders for now — later waves (19/20/21/22) swap each
      // body in-place. Sidebar group gated MANAGE_SHIPPING; the Rate Table
      // child link is hidden without VIEW_FINANCIAL_COST (Task
      // 18). BE re-checks every cost endpoint server-side (API authoritative).
      // Literal child segments register BEFORE the index redirect so they win
      // react-router's first-match resolution.
      { path: '/shipping', element: <Navigate to="/shipping/price" replace /> },
      { path: '/shipping/price', element: withSuspense(<ShippingPriceRoute />) },
      {
        path: '/shipping/track-routing',
        element: withSuspense(<ShippingTrackRoutingPage />),
      },
      { path: '/shipping/config', element: withSuspense(<ShippingConfigPage />) },
      {
        // Legacy 4.7 rate-table page retired — the sidebar now carries a single
        // Rate Table entry pointing at the 4.8 master-detail Rate page, so old
        // bookmarks land there instead of a duplicate implementation.
        path: '/shipping/rate-table',
        element: <Navigate to="/shipping/rate-table-2/rate" replace />,
      },
      // Phase 4.8 Wave 2 Task 05 — Rate Table 2 (master-detail redesign) route,
      // parallel to the 4.7 `/shipping/rate-table` above (P4.8.D-08). Mirrors the
      // 4.7 child-route entry (same withSuspense wrapper); the 4.7 object is left
      // byte-identical (P4.8.D-20). Sidebar link is VFC-gated in ShippingMenuGroup
      // (P4.8.D-19); the page itself HARD-blocks on the dual VFC + MANAGE_SHIPPING
      // gate (P4.8.D-21) — BE stays authoritative.
      //
      // Phase 4.8 Amendment #1 CR-1 (Task 06) — the 3 big top-level tabs
      // (Routing / Margin / History) are split into SEPARATE sidebar submenus,
      // each with its own native route; Rate becomes an independent submenu
      // keeping its inner sub-tabs. Each per-submenu route mounts RateTable2Page
      // with the matching `section` prop, so only that section renders and
      // deep-link + back/forward work natively per submenu (P4.8.D-06). Literal
      // child segments register BEFORE the parent redirect so react-router's
      // first-match resolves them; the bare parent /shipping/rate-table-2
      // redirects → /rate (soft-default section).
      // vg-binding: CONTEXT:decisions=P4.8.D-06,P4.8.D-08,P4.8.D-19
      {
        path: '/shipping/rate-table-2/rate',
        element: withSuspense(<ShippingRateTable2Page section="rate" />),
      },
      {
        path: '/shipping/rate-table-2/routing',
        element: withSuspense(<ShippingRateTable2Page section="routing" />),
      },
      {
        path: '/shipping/rate-table-2/margin',
        element: withSuspense(<ShippingRateTable2Page section="margin" />),
      },
      {
        path: '/shipping/rate-table-2/history',
        element: withSuspense(<ShippingRateTable2Page section="history" />),
      },
      {
        path: '/shipping/rate-table-2',
        element: <Navigate to="/shipping/rate-table-2/rate" replace />,
      },
      // Notifications + Profile
      { path: '/notifications', element: withSuspense(<NotificationsFeedPage />) },
      { path: '/profile', element: <Navigate to="/settings/profile" replace /> },
      { path: '/profile/account-settings', element: <Navigate to="/settings/profile" replace /> },
      { path: '/profile/preferences', element: <Navigate to="/settings/profile?tab=preferences" replace /> },
      { path: '/profile/sessions', element: withSuspense(<SessionsPage />) },
      // Vendors — extended P8.1 routes; /vendors/pending already exists above.
      { path: '/vendors/create', element: <VendorCreateWizardPage /> },
      // dbg (2026-07-07 #294) — Bulk Import wizard. Static segment must
      // precede /vendors/:vendorId so "import" is not read as a vendorId.
      { path: '/vendors/import', element: withSuspense(<VendorBulkImportPage />) },
      // dbg (2026-07-06) — static routes MUST precede /vendors/:vendorId so
      // these segments don't get captured as a vendorId (UAT #302-308).
      { path: '/vendors/kyc', element: <KycQueuePage /> },
      { path: '/vendors/cost-approvals', element: <CostApprovalQueuePage /> },
      // Phase 4.4.2 Wave 5 Task 18 — before /vendors/:vendorId so "allocations"
      // is not captured as a vendorId (static-route-order).
      {
        path: '/vendors/allocations',
        element: withSuspense(<VendorAllocationsPage />),
      },
      // Phase 17 Wave 8 Task 30 — before /vendors/:vendorId so "routing" is not
      // captured as a vendorId (static-route-order, dbg 2026-07-06). RBAC is
      // enforced inside the page: without orders:manage_assignment_rules it
      // renders the admin 403 page rather than redirecting (P17.D-21).
      {
        path: '/vendors/routing',
        element: withSuspense(<VendorRoutingPage />),
      },
      { path: '/vendors/:vendorId', element: <VendorDetailPage /> },
      { path: '/vendors/:vendorId/edit', element: <VendorEditPage /> },
      // dbg-admin-vendor-product-detail (2026-06-19) — admin view of a vendor-pushed product.
      { path: '/vendors/:vendorId/products/:productId', element: <VendorProductDetailPage /> },
      // Audit log viewer — keep placeholder since the canonical audit page
      // is /admin/audit-log (already mounted above with AuditLogPage).
      { path: '/audit', element: <Navigate to="/admin/audit-log" replace /> },

      // ================================================================
      // Phase 8.2 Wave 9 Task 56 — full sidebar wiring (lesson F-007).
      // Every Phase 8.2 page reachable from sidebar via the route table
      // below. Permission gates land at AuthGuard via lazy/loader layer;
      // pages also enforce client-side when mutations happen. BE re-checks
      // on every request.
      // ================================================================

      // --- Finance — admin/billing namespace aliases (sub-group).
      // Existing /billing/* routes already mapped above; mirror under
      // /admin/billing/* so Finance > Billing sidebar URLs resolve
      // without forcing a route-list rewrite of historical links.
      { path: '/admin/billing', element: <Navigate to="/admin/billing/invoices" replace /> },
      { path: '/admin/billing/invoices', element: <InvoiceListPage /> },
      { path: '/admin/billing/invoices/manual-generate', element: <ManualGeneratePage /> },
      { path: '/admin/billing/aging-report', element: <AgingReportPage /> },
      { path: '/admin/billing/credit-memos', element: <CreditMemoListPage /> },
      { path: '/admin/billing/refund-tasks', element: <RefundTaskQueuePage /> },
      { path: '/admin/billing/transactions', element: withSuspense(<TransactionsPage />) },
      { path: '/transactions/:id', element: withSuspense(<TransactionDetailPage />) },
      { path: '/admin/billing/payouts', element: <PayoutsQueuePage /> },
      { path: '/admin/billing/topup-reviews', element: <TopupReviewQueuePage /> },
      // /admin-prefixed alias so the sidebar entry matches its siblings while
      // the bare /billing/topup-queue path from UAT-FIN-014 keeps working.
      { path: '/admin/billing/topup-queue', element: <TopupQueuePage /> },
      { path: '/admin/billing/credits', element: <AdminCreditsPage /> },
      { path: '/admin/billing/gateways', element: <GatewaySettingsPage /> },
      { path: '/admin/billing/transfer-groups', element: <TransferGroupsPage /> },
      { path: '/admin/billing/merchant/:merchantId', element: <MerchantBillingPage /> },

      // --- Finance — Legal Entities + Payment Requests root nav alias.
      { path: '/admin/finance/legal-entities', element: <LegalEntitiesPage /> },
      // Detail page reuses LegalEntitiesPage which already opens an edit
      // drawer keyed by URL (Phase 3.5 Wave 7 Task 21). Keep route stable
      // for sidebar deep-link.
      { path: '/admin/finance/legal-entities/:id', element: <LegalEntitiesPage /> },

      // --- Products group (sidebar) — Phase 8.2 catalog routes.
      { path: '/products', element: <Navigate to="/products/categories" replace /> },
      // /products/categories already wired above.
      { path: '/products/blueprints', element: withSuspense(<BlueprintsListPage />) },
      { path: '/products/blueprints/create', element: withSuspense(<BlueprintCreateWizard />) },
      { path: '/products/blueprints/:blueprintId', element: withSuspense(<BlueprintDetailPage />) },
      // dbg-wizard-iter4 (promote) — Options entity admin page.
      { path: '/products/options', element: withSuspense(<OptionsPage />) },
      // /products/product-approval already wired above.
      { path: '/products/products-special', element: withSuspense(<ProductsSpecialPage />) },
      {
        path: '/products/products-special/:id',
        element: withSuspense(<ProductsSpecialDetailPage />),
      },
      { path: '/products/bundles', element: withSuspense(<BundlesPage />) },
      { path: '/products/mapping', element: withSuspense(<MappingPage />) },
      // dbg-20260803-025413 — Collection is now the second tab of
      // /products/categories. Redirect keeps the sidebar entry + any bookmark
      // working instead of 404-ing after CollectionsPage was retired.
      {
        path: '/products/collections',
        element: <Navigate to="/products/categories?tab=collection" replace />,
      },
      { path: '/products/add-ons', element: withSuspense(<AddonsPage />) },
      // /products/pricing kept (Tiers / Deals / History tabs) but de-linked
      // from the sidebar — Pricing is now a top-level group (see below).
      { path: '/products/pricing', element: withSuspense(<PricingPage />) },
      // Phase 8.2.1 debug — top-level Pricing module. /pricing redirects to the
      // first sub-page; Blueprint Prices (read-only view) + Shipping Fees
      // (canonical editor) get their own routes. Blueprint Cost is pending.
      // vg-binding: CONTEXT:decisions (P8.D-557 — "Blueprint Prices" → "Prices" rename + /pricing/prices route + backward-compat redirect)
      { path: '/pricing', element: <Navigate to="/pricing/prices" replace /> },
      { path: '/pricing/prices', element: withSuspense(<SkuPricesPage />) },
      // Phase 8.2.1 Amendment #16 — Prices V2 kept beside the SKU prices page above (legacy pivot view).
      // vg-binding: CONTEXT:decisions (P8.D-567 — additive /pricing/prices-v2 route, no redirect, no removal)
      { path: '/pricing/prices-v2', element: withSuspense(<BlueprintPricesV2Page />) },
      // Backward-compat: old /pricing/blueprint-prices links redirect to /pricing/prices.
      { path: '/pricing/blueprint-prices', element: <Navigate to="/pricing/prices" replace /> },
      // Retired: Pricing > Shipping Fees duplicated Shipping > Shipping Price.
      // Old link redirects to the canonical /shipping/price (amendment #14).
      // vg-binding: CONTEXT:decisions (P8.D-561 — retire Pricing "Shipping Fees" leaf; route → Navigate /shipping/price; ShippingFeesPage file retained, unrouted)
      { path: '/pricing/shipping-fees', element: <Navigate to="/shipping/price" replace /> },
      { path: '/pricing/blueprint-cost', element: withSuspense(<BlueprintCostPage />) },
      // Phase 14.1 Task 38 (F-O1) — Admin packing-group approval queue (G-32).
      // Gate: MANAGE_PRICING (sidebar leaf + page + BE requireAdminPermission).
      { path: '/pricing/packing-groups', element: withSuspense(<AdminPackingGroupQueue />) },
      // Phase 14.1 (F-O2 / G-03) — pricing config (seasonal / handling / emb fees).
      { path: '/pricing/config', element: withSuspense(<PricingConfigPage />) },
      { path: '/products/techniques', element: withSuspense(<TechniquesPage />) },
      { path: '/products/techniques/new', element: withSuspense(<TechniqueCreatePage />) },
      { path: '/products/techniques/:id', element: withSuspense(<TechniqueDetailPage />) },
      { path: '/products/templates', element: withSuspense(<TemplatesListPage />) },
      // '+ New template' now opens the minimal CreateTemplateDialog modal on
      // TemplatesPage again (reverted the multi-step /new wizard per user
      // request — dbg f2d812af). No /products/templates/new route.
      // Template detail editor (full PUT replace). Added via
      // /vg:debug session f931af39 — replaces the prior alias back to the
      // list page that swallowed every Name-link click.
      { path: '/products/templates/:id', element: withSuspense(<TemplateDetailPage />) },
      // Phase 8.5 — Artwork Doctor catalog pages.
      { path: '/products/materials', element: withSuspense(<MaterialsPage />) },
      { path: '/products/region-errors', element: withSuspense(<RegionErrorsPage />) },
      { path: '/products/check-types', element: withSuspense(<CheckTypesPage />) },
      // Phase 8.4 Amendment #2 — Print Area catalog page.
      // vg-binding: CONTEXT:P8.4.D-51
      { path: '/products/print-areas', element: withSuspense(<PrintAreasPage />) },
      //  (Amendment #2 promoted) — global FulfillmentLocation CRUD.
      { path: '/products/locations', element: withSuspense(<LocationsPage />) },
      { path: '/products/locations/:id', element: withSuspense(<LocationDetailPage />) },
      // Legacy vendor-mapping routes → /vendors/allocations directly. The old
      // targets ('/vendors/settings/mapping') are themselves redirects, so
      // pointing here avoids a double hop. Retired (batch E follow-up).
      { path: '/vendors/mapping', element: <Navigate to="/vendors/allocations" replace /> },
      { path: '/products/vendor-mapping', element: <Navigate to="/vendors/allocations" replace /> },

      // --- System group — Users + Roles dedicated routes.
      { path: '/system', element: <Navigate to="/system/users" replace /> },
      { path: '/system/users', element: <AdminUsersPage /> },
      { path: '/system/users/:id', element: <AdminUserDetailPage /> },
      { path: '/system/roles', element: <AdminRolesPage /> },

      // Phase 8.3 Wave 6 Task 41 — System Settings 7-tab shell + first
      // three tabs (General / Merchant Configs / Vendor Configs). Sibling
      // tasks 42/43 own the remaining tabs (Order / Notifications /
      // Security / Support) — placeholders render today so the strip is
      // complete and reachable from the sidebar.
      // vg-binding: CONTEXT:decisions=P8.D-100,P8.D-101,P8.D-120,P8.D-170
      // vg-binding: INTERFACE-STANDARDS:error-shape
      { path: '/system/settings', element: <SystemSettingsPage /> },
      { path: '/system/settings/general', element: <SystemSettingsPage /> },
      {
        path: '/system/settings/merchant-configs',
        element: <SystemSettingsPage />,
      },
      {
        path: '/system/settings/vendor-configs',
        element: <SystemSettingsPage />,
      },
      {
        path: '/system/settings/order-configs',
        element: <SystemSettingsPage />,
      },
      {
        path: '/system/settings/notifications',
        element: <SystemSettingsPage />,
      },
      { path: '/system/settings/security', element: <SystemSettingsPage /> },
      { path: '/system/settings/support', element: <SystemSettingsPage /> },

      // Phase 8.3 Wave 6 Task 51 — Bug Tracker SA-only page (Kanban /
      // Table + drag-drop status + auto-reported errors banner). BE
      // gate `system:bug_tracker` (task-18); page also surfaces a 403
      // banner if FE perm check fails. Consumes task-18 BE.
      // vg-binding: CONTEXT:decisions=P8.D-126,P8.D-164
      // vg-binding: INTERFACE-STANDARDS:error-shape
      { path: '/system/bug-tracker', element: withSuspense(<BugTrackerPage />) },

      // Phase 8.3 Wave 8 Task 63 — CMS Seller Survey (Config | Results
      // tabs). Sidebar group "Home Management" → "Seller Survey" deep-links
      // here; the page mirrors active tab to `?tab=`. Consumes task-34
      // BE — permission home_management:manage enforced server-side
      // (FE renders a "no permission" card on permission miss).
      // vg-binding: CONTEXT:decisions=P8.D-426,P8.D-427,P8.D-428,P8.D-429,P8.D-430,P8.D-431
      // vg-binding: INTERFACE-STANDARDS:error-shape
      {
        path: '/home-management/seller-survey',
        element: withSuspense(<SellerSurveyPage />),
      },

      // -------------------------------------------------------------------
      // Phase 8.3 — Routes for the 25 admin pages exposed in `layouts/
      // sidebar/groups.ts` but missing from this router until /vg:debug 8.3
      // Cluster A (2026-05-18). AnalyticsPage deferred (broken internal
      // imports — see lazy declarations above).
      // vg-binding: CONTEXT:decisions=P8.D-16,P8.D-18,P8.D-132,P8.D-382,P8.D-475
      // -------------------------------------------------------------------

      // Reports group (Phase 8.3 Wave 5 task-37/38 + Wave 2 task-10).
      // /reports/audit-logs already wired earlier via ReportsAuditLogsPage.
      { path: '/reports/analytics', element: withSuspense(<ReportsAnalyticsPage />) },
      { path: '/reports/warnings', element: withSuspense(<ReportsOrderWarningsPage />) },
      { path: '/reports/sync-errors', element: withSuspense(<ReportsSyncErrorReportsPage />) },
      { path: '/reports/export-center', element: withSuspense(<ReportsExportCenterPage />) },

      // Home Management group (Phase 8.3 Wave 7-8 — CMS sub-modules).
      { path: '/home-management/homepage', element: withSuspense(<HomepagePage />) },
      { path: '/home-management/ebooks', element: withSuspense(<EbooksPage />) },
      { path: '/home-management/blog', element: withSuspense(<BlogPage />) },
      // Blog post editor drill children (not in sidebar; server enforces home_management:manage). dbg #2748.
      { path: '/home-management/blog/posts/new', element: withSuspense(<CmsBlogPostForm />) },
      { path: '/home-management/blog/posts/:postId', element: withSuspense(<CmsBlogPostForm />) },
      { path: '/home-management/help', element: withSuspense(<HelpCenterPage />) },
      // Article editor drill children (not in sidebar; server enforces home_management:manage).
      { path: '/home-management/help/articles/new', element: withSuspense(<CmsHelpArticleForm />) },
      { path: '/home-management/help/articles/:articleId', element: withSuspense(<CmsHelpArticleForm />) },
      { path: '/home-management/stories', element: withSuspense(<StoriesPage />) },
      { path: '/home-management/faq', element: withSuspense(<FaqPage />) },
      { path: '/home-management/footer', element: withSuspense(<FooterPage />) },
      { path: '/home-management/contact', element: withSuspense(<ContactPage />) },
      { path: '/home-management/pages', element: withSuspense(<PagesPage />) },

      // Inventory group (Phase 8.3 Wave 6 task-50 + G-23 sub-reports wired
      // 2026-07-30; paths mirror the report pages' own conventions).
      { path: '/inventory/overview', element: withSuspense(<InventoryOverviewPage />) },
      { path: '/inventory/reports/stock-level', element: withSuspense(<InventoryStockLevelReportPage />) },
      { path: '/inventory/reports/valuation', element: withSuspense(<InventoryValuationReportPage />) },
      { path: '/inventory/reports/movement', element: withSuspense(<InventoryMovementReportPage />) },
      { path: '/inventory/reports/aging', element: withSuspense(<InventoryAgingReportPage />) },
      { path: '/inventory/reports/turnover', element: withSuspense(<InventoryTurnoverReportPage />) },

      // Settings group (Phase 8.3 Wave 6 task-44/51).
      { path: '/settings/email-templates', element: withSuspense(<SettingsEmailTemplatesPage />) },
      // Editor drill children (not in sidebar; server enforces email_template perms). dbg #2663.
      { path: '/settings/email-templates/new', element: withSuspense(<SettingsEmailTemplateEditor />) },
      { path: '/settings/email-templates/:templateId', element: withSuspense(<SettingsEmailTemplateEditor />) },
      { path: '/settings/roles', element: withSuspense(<SettingsRolesPage />) },

      // Phase 10 review-resume F-02 (round-2) — Sales + Affiliate routes
      // (deferred wiring closed in review). All pages live under
      // apps/admin/src/pages/{Sales,Affiliate}/* — built Phase 10 waves
      // 7-13 but never wired to the canonical App.tsx router.
      { path: '/sales/overview', element: withSuspense(<SalesOverviewPage />) },
      { path: '/sales/leads', element: withSuspense(<AdminLeadsPage />) },
      { path: '/sales/leads/:leadId', element: withSuspense(<LeadDetailPage />) },
      { path: '/sales/deals', element: withSuspense(<AdminDealsPage />) },
      { path: '/sales/deals/:dealId', element: withSuspense(<DealDetailPage />) },
      { path: '/sales/groups', element: withSuspense(<SalesGroupsPage />) },
      { path: '/sales/calendar', element: withSuspense(<SalerCalendarPage />) },
      { path: '/sales/targets', element: withSuspense(<SalesTargetsPage />) },
      { path: '/sales/leaderboard', element: withSuspense(<SalesLeaderboardPage />) },
      { path: '/sales/gmv-report', element: withSuspense(<GmvReportPage />) },
      // Phase 10.2 — Sales Operations page (3 internal tabs, URL-synced
      // ?tab&from&to&page&sort). BE enforces 403 for sales_operations:read.
      { path: '/sales/operations', element: withSuspense(<OperationsPage />) },
      { path: '/sales/my-performance', element: withSuspense(<SalerPerformancePage />) },
      { path: '/sales/my-profile', element: withSuspense(<SalerProfilePage />) },
      { path: '/sales/merchants', element: withSuspense(<SalesMerchantListPage />) },
      { path: '/sales/benefits', element: withSuspense(<AdminBenefitsPage />) },
      { path: '/sales/reports', element: withSuspense(<SalesReportsPage />) },
      { path: '/admin/settings/sales', element: withSuspense(<SalesSettingsPage />) },
      { path: '/admin/quotation-templates/:id', element: withSuspense(<QuotationTemplateEditorPage />) },
      { path: '/affiliate', element: withSuspense(<AffiliateAdminPage />) },
      { path: '/affiliate/list', element: withSuspense(<AffiliateListPage />) },
      { path: '/affiliate/:affiliateId', element: withSuspense(<AffiliateDetailPage />) },
      { path: '/affiliate/payout-queue', element: withSuspense(<PayoutQueuePage />) },
      { path: '/affiliate/short-links', element: withSuspense(<ShortLinksAdminPage />) },
      { path: '/affiliate/abuse-report', element: withSuspense(<AbuseReportPage />) },

      // System group (Phase 8.3 Wave 1-5 — admin/maintenance/feature-flags).
      { path: '/system/announcements', element: withSuspense(<SystemAnnouncementsPage />) },
      { path: '/system/dual-approval', element: withSuspense(<SystemDualApprovalQueuePage />) },
      { path: '/system/admin-mgmt', element: withSuspense(<SystemAdminMgmtPage />) },
      { path: '/system/feature-flags', element: withSuspense(<SystemFeatureFlagsPage />) },
      { path: '/system/health', element: withSuspense(<SystemHealthPage />) },
      { path: '/system/maintenance-mode', element: withSuspense(<SystemMaintenanceModePage />) },

      // Monitoring group (Phase 8.3 Wave 3 task-24 + Wave 5).
      { path: '/monitoring/queue-jobs', element: withSuspense(<MonitoringQueueJobsPage />) },
      { path: '/monitoring/sync-errors', element: withSuspense(<MonitoringSyncErrorsPage />) },
      { path: '/monitoring/imap', element: withSuspense(<MonitoringImapParserPage />) },

      // --- Support group (Phase 8.2 Wave 8 tasks 53/54/55).
      { path: '/support', element: <Navigate to="/support/dashboard" replace /> },
      { path: '/support/dashboard', element: withSuspense(<SupportDashboardPage />) },
      // Support tickets list + detail (Phase 8.2 Wave 8 tasks 53/54). Both
      // lazy-loaded so the support module doesn't ship in dashboard cold-start.
      { path: '/support/tickets', element: withSuspense(<SupportTicketsListPage />) },
      { path: '/support/tickets/:id', element: withSuspense(<SupportTicketDetailPage />) },
      { path: '/support/departments', element: withSuspense(<DepartmentQueuePage />) },
      { path: '/support/performance', element: withSuspense(<SupportPerformancePage />) },
      { path: '/support/configurations', element: withSuspense(<SupportConfigurationsPage />) },
    ],
  },

  { path: '*', element: <NotFound /> },
]);

/**
 * Cmd+K palette host — Phase 8.1 Wave 3 Task 14.
 *
 * Owns the open/close state for the lazy-loaded <CommandPalette/>. Hotkey
 * is a global ⌘K / Ctrl+K listener (see useCommandPaletteHotkey). The
 * cmdk chunk is fetched on first open so the initial admin bundle stays
 * lean (FCP <1s). Toggle semantics: ⌘K while open closes; ESC
 * inside the dialog also closes (cmdk built-in focus trap).
 */
function CommandPaletteHost(): JSX.Element {
  const [open, setOpen] = useState(false);
  const toggle = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);
  const close = useCallback(() => {
    setOpen(false);
  }, []);
  useCommandPaletteHotkey(toggle);

  if (!open) return <></>;
  return (
    <Suspense fallback={null}>
      <CommandPalette open={open} onClose={close} />
    </Suspense>
  );
}

export function App(): JSX.Element {
  // Phase 8.3 Wave 6 Task 51 — global ErrorBoundaryAutoReport wraps the
  // router + Cmd+K palette so render-time errors anywhere in the SPA
  // post a bug report to /api/v1/admin/bug-tracker/auto-report before
  // surfacing the fallback UI. Anonymous payloads allowed.
  // vg-binding: CONTEXT:decisions=P8.D-126,P8.D-164
  // vg-binding: INTERFACE-STANDARDS:error-shape
  return (
    <ErrorBoundaryAutoReport>
      <RouterProvider router={router} />
      {/* CommandPaletteHost moved INTO AdminAppShellLayout (issue #2660) —
          it needs the router context for useNavigate(). */}
    </ErrorBoundaryAutoReport>
  );
}
